/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import ApprovalStatus from '../Modals/ApprovalStatus';
import { Modal } from '@mui/material';
import CasesAPI from 'api/CasesAPI';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import EmailAPI from 'api/EmailAPI';
import { handleUpdateTicketNotification } from 'utils/NotificationsFuncs';
import { isReScreenableDocType } from 'utils/reScreening';
import StreamDisplay from 'components/TradeHelper/StreamDisplay';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import pako from 'pako';
import { format } from 'date-fns';
import { formatDate } from 'utils/dateFormater';
import { formatLicenseExceptions } from 'utils/formatLicenseExceptions';
import {
  screeningTicketTariffPDF,
  unifiedTicketReportPDF,
} from 'components/Common/EntityTicketPdf';
import { exportToCSV } from 'components/Common/CSVManage';
import TarrifResults from 'components/TariffChecker/TariffResults';
import AIEvalProduct from 'components/Screening/searchComponents/AIEvalProduct';
import EARScreeningResults from 'components/Classify/EARScreeningResults';
import { Buffer } from 'buffer';
import EntityScreeningResultsTable from 'components/Screening/tables/entityTable/EntityScreeningResultsTable';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import JurisdictionDispositionBadge, {
  CoverageStatusChip,
  CoverageRegime,
  coverageStatusPlain,
} from 'components/Classify/JurisdictionDispositionBadge';
import AssessmentStatusBadge, {
  NotAssessedSummary,
} from 'components/Classify/AssessmentStatusBadge';
import {
  combinedNuclearHeadline,
  isPhaseNotAssessed,
  isPhaseOrPartNotAssessed,
  isPhaseNotRequested,
  notAssessedPhases,
} from 'utils/exportAssessment';
import type { ExportAssessmentPhase } from 'interfaces/iStreamedResponse';
import TariffPage from 'pages/tariffs/TariffPage';
import 'resources/styles/chatkit-classify.css';
import { isOriginMpfExempt } from 'utils/mpfExemptions';
import { EEI_VALUE_EXEMPTION_NOTE, showEeiValueExemptionNote } from 'utils/eeiValueNote';
import ScopedReviewSummary from 'components/Classify/ScopedReviewSummary';
import { NOT_REQUESTED_SECTION_TEXT, scopedReviewSummary } from 'utils/scopedReviewCaveat';
import { scheduleBCodeRows } from 'utils/scheduleBCodeResult';
import {
  savedExportDescription,
  savedExportOpeningMessage,
} from 'utils/exportChatTranscript';
import { engineFigures } from 'utils/tariffFigures';

type CrossRulingLink = {
  title: string;
  url: string;
  relevance_percent: number | null;
  relevance_note: string | null;
};

const normalizeCrossRuling = (item: any): CrossRulingLink | null => {
  if (typeof item === 'string') {
    return {
      title: item,
      url: item,
      relevance_percent: null,
      relevance_note: null,
    };
  }

  if (!item || typeof item !== 'object') {
    return null;
  }

  const url = item?.url ?? item?.href ?? '';
  const relevancePercent =
    typeof item?.relevance_percent === 'number' ? item.relevance_percent : null;

  if (!url) {
    return null;
  }

  return {
    title: item?.title ?? url ?? 'Untitled ruling',
    url: url,
    relevance_percent: relevancePercent,
    relevance_note: item?.relevance_note ?? null,
  };
};

const isUrlOnlyCrossRuling = (item: CrossRulingLink) =>
  (!item.title || item.title === item.url) &&
  item.relevance_percent === null &&
  !item.relevance_note;

const getChatMessageText = (message: any) => {
  if (typeof message?.text === 'string') {
    return message.text;
  }

  if (typeof message?.content === 'string') {
    return message.content;
  }

  if (Array.isArray(message?.content)) {
    return message.content
      .map((part: any) => {
        if (typeof part === 'string') return part;
        if (typeof part?.text === 'string') return part.text;
        if (typeof part?.text?.value === 'string') return part.text.value;
        if (typeof part?.content === 'string') return part.content;
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }

  return '';
};

const formatChatRole = (role: string) => {
  if (role === 'user') return 'User';
  if (role === 'assistant') return 'BITE Assistant';
  if (role === 'tool') return 'Tool Output';
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Message';
};

const formatDisplayValue = (value: any) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value === null || value === undefined) return '';
  return JSON.stringify(value, null, 2);
};

interface IClassResultsProps {
  cur_user: any;
  cur_case: any;
  trigger: boolean;
  setTrigger: any;
  triggerCSV: boolean;
  setTriggerCSV: any;
  triggerSendPdf?: boolean;
  setTriggerSendPdf?: any;
  sendPdfFileName?: string;
  sendPdfEmailRecipients?: string[];
  onTariffPdfSendComplete?: (success: boolean) => void;
  whitelistEntity: (content: any) => void;
  setShareModal: any;
  setIsCommentsModalOpen: any;
}

const ConsolidatedResults: React.FC<IClassResultsProps> = ({
  cur_user,
  cur_case,
  trigger,
  setTrigger,
  triggerCSV,
  setTriggerCSV,
  triggerSendPdf = false,
  setTriggerSendPdf,
  sendPdfFileName,
  sendPdfEmailRecipients = [],
  onTariffPdfSendComplete,
  whitelistEntity,
  setShareModal,
  setIsCommentsModalOpen,
}) => {
  const history = useHistory();
  const [openFilter, setOpenFilter] = useState(false);
  const [activeFilters, setActiveFilters] = useState('');
  const [activeFilter, setActiveFilter] = useState(
    cur_case.data.activeFilter ? cur_case.data.activeFilter : 'BITE Lens',
  );
  const componentRef = useRef<HTMLDivElement>(null);
  const unifiedPdfRef = useRef<HTMLDivElement>(null);
  const htsFinalReportRef = useRef<HTMLDivElement>(null);

  if (!cur_case) {
    history.push('/bite-monitor');
    return <></>;
  }
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandedCross, setIsExpandedCross] = useState(false);
  const [isExpandedSecNotes, setIsExpandedSecNotes] = useState(false);
  const [isPdfExpandingUnified, setIsPdfExpandingUnified] = useState(false);
  const [chatHistoryMaxHeight, setChatHistoryMaxHeight] = useState<
    number | null
  >(null);

  const { enqueueSnackbar } = useSnackbar();

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const [isFrequencyModalOpen, setIsFrequencyModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedOpts, setSelectedOpts] = useState([]);
  const [curLevel, setCurLevel] = useState(2);
  const [totalCost, setTotalCost] = useState('');
  const [totalAddCost, setTotalAddCost] = useState('');
  const [totalBaseCost, setTotalBaseCost] = useState('');
  const [filterOpts, setFilterOpts] = useState<any>({});
  const [screenParmsModal, setScreenParamsModal] = useState(false);
  const [newTab, setNewTab] = useState(false);
  const [curVersion, setCurVersion] = useState<any>(
    cur_case.data.results.length > 0
      ? cur_case.data.results[cur_case.data.results.length - 1]
      : undefined,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [productTablePage, setProductTablePage] = useState(0);
  const [currentItems, setCurrentItems] = useState<any[]>([]);
  const [removeTrigger, setRemoveTrigger] = useState(false);
  const [aiSums, setAISums] = useState<any>([]);

  const decompressData = (compressedData: any) => {
    if (compressedData === undefined || compressedData.length === 0) {
      return {};
    }
    const compressedBuffer = Buffer.from(compressedData[0], 'base64');
    const decompressed = pako.ungzip(compressedBuffer, { to: 'string' });
    return JSON.parse(decompressed);
  };

  const [passedDownResults, setPassedDownResults] = useState<any[]>([]);
  useEffect(() => {
    const decompData =
      cur_case.data.results.length > 0
        ? decompressData(
            cur_case.data.results[cur_case.data?.results.length - 1].allResults,
          )
        : [];

    setPassedDownResults(decompData);
  }, [cur_case.data.results]);

  const flattenSavedDocs = (items: any[]) =>
    Array.isArray(items)
      ? items.flatMap((item: any) =>
          Array.isArray(item?.docs) ? item.docs : item ? [item] : [],
        )
      : [];

  const preparedBy =
    [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    cur_user?.basics?.email ||
    'BITE Tariff';

  const buildTariffReportData = () => ({
    ticketName: cur_case.data.ticketName,
    docType: cur_case.data.docType,
    tariffResults: cur_case.data.tariffResults || {},
    preparedBy: preparedBy,
  });

  const productScreeningResults = useMemo(() => {
    const docs = flattenSavedDocs(passedDownResults);
    const hits = docs.map((doc: any) =>
      doc?._source ? doc : { _source: doc },
    );

    return {
      isLoading: false,
      data: [
        {
          result: {
            body: {
              hits: {
                hits: hits,
                total: { value: hits.length },
              },
            },
          },
        },
      ],
    };
  }, [passedDownResults]);

  const buildUnifiedReportData = () => {
    const exactMessages =
      cur_case.data?.htsChatHistory?.chatkitThread?.available &&
      Array.isArray(cur_case.data?.htsChatHistory?.chatkitThread?.messages)
        ? cur_case.data.htsChatHistory.chatkitThread.messages
        : [];
    const observedMessages = Array.isArray(
      cur_case.data?.htsChatHistory?.observed?.messages,
    )
      ? cur_case.data.htsChatHistory.observed.messages
      : [];
    const savedChatMessages =
      exactMessages.length > 0 ? exactMessages : observedMessages;
    const productDocs = flattenSavedDocs(passedDownResults).map((doc: any) =>
      doc?._source ? doc._source : doc,
    );
    const commodity = cur_case.data?.commodity || {};

    // For Export Unified tickets, the "classification description" / product name should be
    // the product description captured in the workflow payload — NOT the "Save Work"
    // placeholder or a stray intake answer (e.g. an order-of-review option number like "3").
    // Prefer the workflow's own product.description, then the Schedule B product name.
    const wf = Array.isArray(cur_case.data?.workflowData)
      ? cur_case.data.workflowData[0] || {}
      : cur_case.data?.workflowData || {};
    const isExportUnified = cur_case.data?.docType === 'Export Unified';
    const norm = (v: any) =>
      v === null || v === undefined ? '' : String(v).trim();
    // The fact ledger is the authoritative product description (set on both the
    // sequential and jump-to-regime paths). Read it from the saved workflow state so
    // even tickets saved before the backend fix render the right description here.
    let factProductDesc = '';
    try {
      const rawState = wf?._workflowStateJson;
      const parsedState =
        typeof rawState === 'string' ? JSON.parse(rawState) : rawState;
      factProductDesc = norm(parsedState?.facts?.product_description?.value);
    } catch {
      /* ignore malformed saved state */
    }
    const exportProductDesc =
      factProductDesc ||
      norm(wf?.product?.description) ||
      norm(wf?.schedule_b?.product_name) ||
      norm(commodity.commodityName);
    const classificationDesc = isExportUnified
      ? exportProductDesc || cur_case.data.classificationValue
      : cur_case.data.classificationValue;
    const productDisplayName = isExportUnified
      ? exportProductDesc || commodity.commodityName
      : commodity.commodityName;

    // Prepend the user's verbatim OPENING message as the first entry of the Classification Result
    // record — it was never part of followUps. Export: `savedExportOpeningMessage` (the snapshot's
    // `_initialRequest`, else the server-echoed product_desc in the saved stream, else
    // classificationValue unless it is a command label like "Save Work" — Bug 9). exportProductDesc
    // is the DERIVED description, so it is NOT used here.
    const savedFollowUps = Array.isArray(cur_case.data.followUps)
      ? cur_case.data.followUps
      : [];
    const openingMessage = isExportUnified
      ? savedExportOpeningMessage(cur_case.data)
      : norm(cur_case.data.initialRequest);
    const openingAlreadyLeads =
      savedFollowUps.length > 0 &&
      (norm(savedFollowUps[0]?.answer) === openingMessage ||
        norm(savedFollowUps[0]?.question) === openingMessage);
    const followUpsWithOpening =
      openingMessage && !openingAlreadyLeads
        ? [{ question: 'Initial request', answer: openingMessage }, ...savedFollowUps]
        : savedFollowUps;

    return {
      ticketName: cur_case.data.ticketName,
      docType: cur_case.data.docType,
      status: cur_case.data.status,
      frequency: cur_case.data.frequency,
      lastScreen: cur_case.data.lastUpdate || cur_case.data.createdAt,
      preparedBy: preparedBy,
      classification: {
        ticketName: cur_case.data.ticketName,
        docType: cur_case.data.docType,
        isLegacyTicket: !cur_case.data?.htsFinalReport,
        classificationValue: classificationDesc,
        commodity: {
          name: productDisplayName,
          codeLabel:
            cur_case.data.docType === 'Export Unified'
              ? 'Schedule B Code'
              : 'HTS Code',
          code: commodity.harmonizedScheduleCode,
          material: commodity.material,
          intendedUse: commodity.intendedUse,
          physicalState: commodity.physicalState,
        },
        followUps: followUpsWithOpening,
        chatHistory: savedChatMessages
          .map((message: any) => ({
            role: message?.role,
            text: getChatMessageText(message),
          }))
          .filter((message: any) => message.text),
        crossRulings: Array.isArray(cur_case.data.crossRulings)
          ? cur_case.data.crossRulings
              .map(normalizeCrossRuling)
              .filter((item: CrossRulingLink | null): item is CrossRulingLink =>
                Boolean(item),
              )
          : [],
        chapterNotes: cur_case.data.chapterNotes,
        finalReport: cur_case.data.htsFinalReport,
        aiStream: cur_case.data.aiStream || [],
        preparedBy: preparedBy,
      },
      workflowData: cur_case.data.workflowData || [],
      tariffResults: cur_case.data.tariffResults || {},
      aiSums: cur_case.data.aiSums || [],
      product: {
        name: productDisplayName,
        htsCode: commodity.harmonizedScheduleCode,
        country: commodity.originCountry || commodity.destinationCountry || '',
        description: classificationDesc,
      },
      productDocs: productDocs,
      productTotal: Number(passedDownResults?.[0]?.hits) || productDocs.length,
    };
  };

  useEffect(() => {
    setProductTablePage(0);
  }, [passedDownResults]);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const resetTable = () => {
    setPassedDownResults(passedDownResults);
    setCurLevel(2);
    setActiveFilter('BITE Lens');
    setSelectedOpts(filterOpts['BITE Lens']);
  };

  const removeHit = async (id: string, name: string) => {
    const user = cur_user.basics || {};
    const existingHits = cur_case.data.removedHits || [];

    const idExists = existingHits.includes(id);

    const updatedHits = idExists
      ? existingHits.filter((hitId: string) => hitId !== id)
      : [...existingHits, id];

    const actObj: any = {
      user: {
        kc_id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      },
      action: idExists
        ? `restored the result: ${name} to the ticket`
        : `removed the result: ${name} from the ticket`,
    };

    const update: any = {
      _id: cur_case.data._id,
      removedHits: updatedHits,
      activity: [...cur_case.data.activity, actObj],
    };

    await CasesAPI.updateCase(update);
    setTrigger(!trigger);
    setRemoveTrigger(!removeTrigger);
  };

  const changeStatus = async (newStatus: string) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `changed the status of the ticket from ${cur_case.data.status} to ${newStatus}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      status: newStatus,
      activity: [...cur_case.data.activity, actObj],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      if (
        cur_case.data.sharedWith.length > 0 &&
        cur_user.basics.Id === cur_case.data.createdBy
      ) {
        cur_case.data.sharedWith.forEach(async (user: string) => {
          handleUpdateTicketNotification(
            cur_case.data.ticketName,
            cur_user,
            user,
            cur_case.data._id,
          );
        });
      } else if (
        cur_case.data.sharedWith.length > 0 &&
        cur_user.basics.Id !== cur_case.data.createdBy
      ) {
        const shareArr = cur_case.data.sharedWith.filter((user: string) => {
          return user !== cur_user.basics.id && user !== cur_user.basics.email;
        });
        shareArr.push(cur_case.data.createdBy);
        shareArr.forEach(async (user: string) => {
          handleUpdateTicketNotification(
            cur_case.data.ticketName,
            cur_user,
            user,
            cur_case.data._id,
          );
        });
      }
    } else {
      enqueueSnackbar('There was an error updating the status of your ticket', {
        variant: 'error',
      });
    }
    setTrigger(!trigger);
  };

  const handleFilterClick = () => {
    const res = filterDataByKey(passedDownResults, activeFilter, selectedOpts);
    setPassedDownResults(res);
  };

  const handleVisClick = (index: number) => {
    if (currentPage > 1) {
      const updatedResults = passedDownResults.filter(
        (res: any, i: number) => i !== index,
      );
      setPassedDownResults(updatedResults);
      if (currentItems.length <= 1) {
        setCurrentPage(currentPage - 1);
      }
    } else {
      const updatedResults = passedDownResults.filter(
        (res: any, i: number) => i !== index,
      );
      setPassedDownResults(updatedResults);
    }
  };

  const extractValuesByKeys = (data: any[], keys: string[]) => {
    // Initialize an object to store the unique values for each key
    const result = keys.reduce((acc: any, key: string) => {
      acc[key] = [];
      return acc;
    }, {});
    // Iterate over the array of objects
    data.forEach((item) => {
      if (Array.isArray(item.docs)) {
        item.docs.forEach((doc: any) => {
          keys.forEach((key) => {
            if (doc[key] && !result[key].includes(doc[key])) {
              result[key].push(doc[key]);
            }
          });
        });
      }
    });

    return result;
  };

  const filterDataByKey = (data: any, key: string, allowedValues: string[]) => {
    return data
      ?.map((item: any) => {
        if (Array.isArray(item?.docs)) {
          return {
            ...item,
            docs: item?.docs?.filter((doc: any) =>
              allowedValues?.includes(doc[key]),
            ),
          };
        }
        return item;
      })
      .filter((item: any) => item?.docs && item?.docs?.length > 0);
  };

  const getOpts = () => {
    const keysToExtract = [
      'BITE Lens',
      'Source Country',
      'Agency',
      'List Name',
    ];
    const result = extractValuesByKeys(passedDownResults, keysToExtract);

    setFilterOpts(result);
    setSelectedOpts(
      cur_case.data.selectedOpts.length > 0
        ? cur_case.data.selectedOpts
        : result['BITE Lens'],
    );
  };

  const headers = ['Field Input', 'Row #', 'Dataset', 'Total Hits'];
  const buildUnifiedPdf = async (name: string, shouldDownload = true) => {
    return await unifiedTicketReportPDF(buildUnifiedReportData(), name, {
      shouldDownload: shouldDownload,
    });
  };

  const handleCSV = async () => {
    try {
      if (cur_case.data.docType === 'Tariff') {
        await screeningTicketTariffPDF(
          buildTariffReportData(),
          `${cur_case.data.ticketName}.pdf`,
        );
      } else if (
        cur_case.data.docType === 'Import Unified' ||
        cur_case.data.docType === 'Export Unified'
      ) {
        await buildUnifiedPdf(`${cur_case.data.ticketName}.pdf`);
      } else if (passedDownResults.length >= 1) {
        exportToCSV(
          `${cur_case.data.ticketName}.csv`,
          passedDownResults,
          headers,
          'monitor',
        );
      } else {
        enqueueSnackbar('No Result Data to Export', { variant: 'error' });
      }
    } finally {
      setTriggerCSV(false);
    }
  };

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const buffer = await blob.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  };

  const handleSendPdf = async () => {
    let sendSucceeded = false;

    try {
      const resolvedFileName = `${
        sendPdfFileName || cur_case.data.ticketName || 'result_ticket'
      }.pdf`;
      let pdfBlob: Blob | undefined = undefined;

      if (cur_case.data.docType === 'Tariff') {
        pdfBlob = await screeningTicketTariffPDF(
          buildTariffReportData(),
          resolvedFileName,
          { shouldDownload: false },
        );
      } else if (
        cur_case.data.docType === 'Import Unified' ||
        cur_case.data.docType === 'Export Unified'
      ) {
        pdfBlob = await buildUnifiedPdf(resolvedFileName, false);
      }

      if (!pdfBlob || sendPdfEmailRecipients.length === 0) {
        throw new Error('Please add at least one recipient email');
      }

      const pdfBase64 = await blobToBase64(pdfBlob);
      const senderName = `${cur_user?.basics?.firstName || ''} ${
        cur_user?.basics?.lastName || ''
      }`.trim();
      const emailRes = await EmailAPI.sendTariffResultPdf({
        recipients: sendPdfEmailRecipients,
        fileName: resolvedFileName,
        pdfBase64: pdfBase64,
        docType: cur_case?.data?.docType,
        ticketId: cur_case?.data?._id,
        ticketName: cur_case?.data?.ticketName,
        senderName: senderName,
        senderEmail: cur_user?.basics?.email,
      });

      if (emailRes?.success) {
        sendSucceeded = true;
        enqueueSnackbar('PDF emailed successfully', { variant: 'success' });
      } else {
        throw new Error(emailRes?.result || 'Failed to send PDF email');
      }
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to send result PDF: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    } finally {
      setTriggerSendPdf?.(false);
      onTariffPdfSendComplete?.(sendSucceeded);
    }
  };

  const handleApprovalCloseModal = () => {
    setIsApprovalModalOpen(false);
  };

  // --- Helpers to adapt saved snapshot to TarrifResults props ---

  const normalizeEnabledExclusions = (val: any): Record<string, boolean> => {
    if (!val) {
      return {};
    }
    // If Mongoose Map
    if (typeof val?.get === 'function' && typeof val?.entries === 'function') {
      const obj: Record<string, boolean> = {};
      for (const [k, v] of (val as Map<string, boolean>).entries()) {
        obj[k] = Boolean(v);
      }
      return obj;
    }
    // If array of entries
    if (Array.isArray(val)) {
      try {
        return Object.fromEntries(val as any);
      } catch {
        /* noop */
      }
    }
    // If plain object already
    if (typeof val === 'object') {
      return val as Record<string, boolean>;
    }
    return {};
  };

  // Build the display object TarrifResults expects from the saved snapshot
  const buildTicketTariffProps = (snap: any) => {
    if (!snap) {
      return null;
    }

    const baseDutyRate = snap.base?.baseDutyRate ?? '';
    const productDesc = snap.base?.productDesc ?? '';
    const lineSplitLabel = snap.base?.lineSplitLabel ?? '';

    const hasSplit = Boolean(snap.results?.hasSplit);
    const summaryRows = snap.results?.summaryRows ?? [];
    const lines = hasSplit ? snap.results?.lines ?? [] : [];

    const additionalDutyRateDisplay =
      snap.math?.additionalDutyRateDisplay ?? '';
    const dutyCost = Number(snap.math?.dutyCost ?? 0);
    const totalCostLocal = Number(
      snap.math?.totalCost ?? Number(snap.inputs?.productValue ?? 0) + dutyCost,
    );

    const productValue = Number(snap.inputs?.productValue ?? 0);
    const materialPct = Number(snap.inputs?.materialPct ?? 0);

    const optionalExclusions = snap.results?.optionalExclusions ?? [];
    const enabledExclusions = normalizeEnabledExclusions(
      snap.results?.enabledExclusions,
    );

    return {
      // The object the component expects as `tariffResults`
      trForComponent: {
        baseDutyRate: baseDutyRate,
        additionalDutyRate: additionalDutyRateDisplay,
        productDesc: productDesc,
        results: summaryRows,
        lines: lines,
        lineSplitLabel: lineSplitLabel,
        optionalExclusions: optionalExclusions,
      },
      // Extra fields passed as separate props
      extra: {
        productValue: productValue,
        dutyCost: dutyCost,
        totalCost: totalCostLocal,
        displayAdditionalRate: additionalDutyRateDisplay,
        tariffCandidates: optionalExclusions,
        enabledExclusions: enabledExclusions,
        materialPct: materialPct,
      },
    };
  };

  // --- Snapshot helpers for the ticket view (read-only) ---
  const savedTR = cur_case?.data?.tariffResults ?? {};
  const extra = savedTR.inputs ?? {};
  const savedLines: any[] = Array.isArray(savedTR.lines) ? savedTR.lines : [];

  const hasSplit =
    (Array.isArray(savedLines) && savedLines.length > 0) || false;

  const needsAmount =
    (savedTR?.results?.requiredAmountUnits?.length ?? 0) > 0 || false;

  // Compute material pct only when a split existed
  const totalLineValue = savedLines.reduce(
    (s, l: any) => s + (Number(l?.value) || 0),
    0,
  );
  const line2 = savedLines.find((l: any) => l?.id === 'line2');

  const computedMaterialPct = hasSplit
    ? extra.materialPct ??
      (line2 && totalLineValue > 0
        ? Math.round((Number(line2.value) / totalLineValue) * 100)
        : undefined)
    : undefined;

  const computedProductValue =
    extra.productValue ?? (totalLineValue > 0 ? totalLineValue : undefined);

  const splitLabel = savedTR.lineSplitLabel || 'Material Percentage';

  const formatMoney = (n: any) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const formatLocalDate = (d: any) => {
    if (!d) {
      return '';
    }
    try {
      return format(new Date(d), 'MM/dd/yyyy');
    } catch {
      return String(d);
    }
  };

  const titleCase = (s: string) =>
    (s || '')
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const countryUi = savedTR?.countryUi;
  const loadingCountryVal = savedTR?.inputs?.loadingCountry;

  // Read dates from snapshot if present
  const effectiveDateStr = extra.effectDate ?? '';
  const loadingDateStr = extra.loadDate ?? '';

  useEffect(() => {
    if (passedDownResults.length > 0) {
      getOpts();
    }
  }, []);

  useEffect(() => {
    if (curVersion) {
      setNewTab(false);
      setCurLevel(2);
      const data = decompressData(curVersion.allResults);
      setPassedDownResults(data);
    }
  }, [curVersion]);

  useEffect(() => {
    if (curVersion) {
      if (newTab) {
        setCurLevel(2);
        const data = decompressData(curVersion.newResults);
        setPassedDownResults(data);
      } else {
        setCurLevel(2);
        const data = decompressData(curVersion.allResults);
        setPassedDownResults(data);
      }
    }
  }, [newTab]);

  useEffect(() => {
    if (Object.keys(passedDownResults).length > 0) {
      handleFilterClick();
    }
  }, [selectedOpts]);

  useEffect(() => {
    if (triggerCSV) {
      handleCSV();
    }
  }, [triggerCSV]);

  useEffect(() => {
    if (triggerSendPdf) {
      handleSendPdf();
    }
  }, [triggerSendPdf]);

  useEffect(() => {
    const initialResponses = cur_case.data.followUps.reduce(
      (acc: any, item: any) => {
        acc[item.question] = item.answer || '';
        return acc;
      },
      {} as Record<string, string>,
    );
    setResponses(initialResponses);
  }, []);

  const isImportUnified = cur_case.data.docType === 'Import Unified';
  const isExportUnified = cur_case.data.docType === 'Export Unified';
  // Export: the Classification Description is the user's verbatim opening message, else the saved
  // product description — never the raw classificationValue (an old ticket could hold a command
  // label like "Save Work" there — Bug 9).
  const exportDescription = isExportUnified
    ? savedExportDescription(cur_case.data)
    : '';
  const crossRulingLinks: CrossRulingLink[] = Array.isArray(
    cur_case.data?.crossRulings,
  )
    ? cur_case.data.crossRulings
        .map(normalizeCrossRuling)
        .filter((item: any): item is CrossRulingLink => Boolean(item))
    : [];
  const visibleCrossRulingLinks = isExpandedCross
    ? crossRulingLinks
    : crossRulingLinks.slice(0, 5);
  const hasOnlyUrlCrossRulings =
    crossRulingLinks.length > 0 && crossRulingLinks.every(isUrlOnlyCrossRuling);
  const htsChatHistory = cur_case.data?.htsChatHistory;
  const exactChatMessages =
    htsChatHistory?.chatkitThread?.available &&
    Array.isArray(htsChatHistory?.chatkitThread?.messages)
      ? htsChatHistory.chatkitThread.messages
      : [];
  const observedChatMessages = Array.isArray(htsChatHistory?.observed?.messages)
    ? htsChatHistory.observed.messages
    : [];
  const chatHistoryMessages =
    exactChatMessages.length > 0 ? exactChatMessages : observedChatMessages;
  const htsFinalReport = cur_case.data?.htsFinalReport;
  // Same test ClassResults.tsx uses: an Import Unified ticket saved before the
  // Claude-managed classification chat existed has none of these fields at all.
  const isLegacyClassificationTicket =
    !htsFinalReport &&
    !htsChatHistory?.threadId &&
    !htsChatHistory?.source &&
    exactChatMessages.length === 0 &&
    observedChatMessages.length === 0;

  useEffect(() => {
    const finalReportElement = htsFinalReportRef.current;

    if (!finalReportElement || !chatHistoryMessages.length || !htsFinalReport) {
      setChatHistoryMaxHeight(null);
      return undefined;
    }

    const updateChatHistoryMaxHeight = () => {
      const height = Math.ceil(
        finalReportElement.getBoundingClientRect().height,
      );
      setChatHistoryMaxHeight(height > 0 ? height : null);
    };

    updateChatHistoryMaxHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateChatHistoryMaxHeight);

      return () => {
        window.removeEventListener('resize', updateChatHistoryMaxHeight);
      };
    }

    const resizeObserver = new ResizeObserver(updateChatHistoryMaxHeight);
    resizeObserver.observe(finalReportElement);

    return () => {
      resizeObserver.disconnect();
    };
    // Unlike ClassResults.tsx, the Classification section here is inside a
    // collapsible panel (isSectionExpanded('Classification')) — its content,
    // including htsFinalReportRef's div, isn't in the DOM at all until the
    // user expands it. Depending only on [chatHistoryMessages.length,
    // htsFinalReport] means this ran once on mount while the panel was still
    // collapsed (finalReportElement null → height stuck at null forever,
    // never re-measured on expand). openFilter/activeFilters/
    // isPdfExpandingUnified are exactly what isSectionExpanded('Classification')
    // is derived from, so re-running on those re-measures once the ref is
    // actually attached.
  }, [
    chatHistoryMessages.length,
    htsFinalReport,
    openFilter,
    activeFilters,
    isPdfExpandingUnified,
  ]);

  const isSectionExpanded = (section: string) =>
    isPdfExpandingUnified || (openFilter && activeFilters === section);
  const isSectionCollapsed = (section: string) => !isSectionExpanded(section);
  const exportWorkflowData = cur_case.data?.workflowData?.[0] || {};
  // Schedule B is skipped (not applicable) for intangible exports — show the reason
  // instead of blank "N/A" candidate fields.
  const scheduleBNotApplicable = Boolean(
    exportWorkflowData?.schedule_b?.not_applicable,
  );
  const scheduleBNaReason =
    exportWorkflowData?.schedule_b?.not_applicable_reason ||
    'Not applicable to this transaction.';
  const eeiData = exportWorkflowData?.eei || {};
  const profileData = exportWorkflowData?.profile || {};
  // One label/value row in the EEI section, matching the tariff-inputs layout the
  // other Export Unified sections use.
  const renderEeiRow = (label: string, value?: string | null, note?: string | null) => (
    <div className='tariff-inputs'>
      <div
        className='classify-header-ticket'
        style={{ color: 'var(--Text-Secondary, var(--text-classify))' }}
      >
        {label}
      </div>
      <div className='classify-cont' style={{ padding: '0px' }}>
        <div className='classify-cont-sec'>
          <div
            className='classify-cont-sec-div'
            style={{
              color: 'var(--text-primary-color)',
              fontWeight: '600',
              fontSize: '16px',
            }}
          >
            {value || 'N/A'}
          </div>
          {note ? (
            <div
              className='classify-cont-sec-div'
              style={{
                color: 'var(--Text-Secondary, var(--text-secondary))',
                fontSize: '13px',
                marginTop: '4px',
              }}
            >
              {note}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  // v2 conditional-routing — a per-regime coverage row (same tariff-inputs shape as
  // renderEeiRow, so it drops into the 2-column NRC/ITAR grids): the CoverageStatusChip +
  // the plain-language why-continued/stopped line. Rendered only when a status is present.
  const renderCoverageRow = (
    label: string,
    status: string | null | undefined,
    regime: CoverageRegime,
  ) => (
    <div className='tariff-inputs'>
      <div
        className='classify-header-ticket'
        style={{ color: 'var(--Text-Secondary, var(--text-classify))' }}
      >
        {label}
      </div>
      <div className='classify-cont' style={{ padding: '0px' }}>
        <div className='classify-cont-sec'>
          <div
            className='classify-cont-sec-div'
            style={{
              color: 'var(--text-primary-color)',
              fontWeight: '600',
              fontSize: '16px',
            }}
          >
            <CoverageStatusChip status={status} />
            <span
              style={{
                display: 'block',
                marginTop: '6px',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: 1.5,
              }}
            >
              {coverageStatusPlain(
                status,
                regime,
                exportWorkflowData?.jurisdiction_routing,
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // v2 conditional-routing — the PACKAGE-level jurisdiction disposition (mirrors pgaData).
  // Self-hides when absent (flag OFF / older saved ticket).
  const jurisdictionData = exportWorkflowData?.jurisdiction_routing;

  // "Where to File" / PGA agency-routing summary (top-level, like `eei`). Advisory only.
  // The whole section self-hides when the routing is absent (older tickets predating it).
  // Cells are laid out TWO-PER-ROW below (results-card-frame + two tariff-inputs), the same
  // 2-column convention the CCL lower sections use, so nothing stacks half-width.
  const pgaData = exportWorkflowData?.pga_routing;
  const pgaAgenciesArr: any[] = Array.isArray(pgaData?.agencies)
    ? pgaData.agencies
    : [];
  const pgaNotAssessedArr: string[] = Array.isArray(pgaData?.not_assessed)
    ? pgaData.not_assessed
    : [];
  const pgaNotRequestedArr: string[] = Array.isArray(pgaData?.not_requested)
    ? pgaData.not_requested
    : [];
  const pgaAgencyLabel = (a?: any): string => {
    const name = a?.agency_display || a?.agency || 'Agency';
    const code = a?.aes_license_type_code;
    return code ? `${name} — AES ${code}` : name;
  };
  const pgaCellBaseStyle: React.CSSProperties = {
    display: 'block',
    maxWidth: '100%',
    color: 'var(--text-primary-color)',
    fontWeight: '600',
    fontSize: '16px',
    whiteSpace: 'pre-line',
  };
  type PgaCell = { label: string; value: string; style: React.CSSProperties };
  // Collapsed = quick summary (determination + plain-language paragraph when present).
  const pgaSummaryCells: PgaCell[] = [
    { label: 'Determination', value: pgaData?.headline || 'N/A', style: pgaCellBaseStyle },
    ...(pgaData?.prose
      ? [{ label: 'Summary', value: String(pgaData.prose), style: pgaCellBaseStyle }]
      : []),
  ];
  // NOTE: Appendix X niche-PGA requirements are rendered inside <EARScreeningResults/> (the shared
  // control-list card of the Country and Control List Check, used both live and here), so there is
  // no separate niche block in this file.
  // Expanded = summary + per-agency filing steps + not-assessed + advisory note.
  const pgaDetailCells: PgaCell[] = [
    ...pgaAgenciesArr.map((a: any) => ({
      label: pgaAgencyLabel(a),
      value:
        [a?.why, a?.next_step, a?.aes_license_type_description]
          .filter(Boolean)
          .join('\n') || 'N/A',
      style: pgaCellBaseStyle,
    })),
    ...(pgaNotAssessedArr.length
      ? [{ label: 'Not assessed', value: pgaNotAssessedArr.join('\n'), style: pgaCellBaseStyle }]
      : []),
    ...(pgaNotRequestedArr.length
      ? [
          {
            label: 'Not requested (scoped review)',
            value: `${pgaNotRequestedArr.join('\n')}\nNot run — treat as open, not cleared.`,
            style: pgaCellBaseStyle,
          },
        ]
      : []),
    ...(pgaData?.caveat
      ? [{ label: 'Note', value: String(pgaData.caveat), style: pgaCellBaseStyle }]
      : []),
  ];
  const pgaChunkPairs = (cells: PgaCell[]): PgaCell[][] => {
    const rows: PgaCell[][] = [];
    for (let i = 0; i < cells.length; i += 2) rows.push(cells.slice(i, i + 2));
    return rows;
  };
  const pgaSummaryRows = pgaChunkPairs(pgaSummaryCells);
  const pgaExpandedRows = pgaChunkPairs([...pgaSummaryCells, ...pgaDetailCells]);
  // Render an array of chunked cell-rows as 2-column results-card-frame rows (mirrors the
  // cclLowerRows render); a lone trailing cell keeps the left column via a spacer.
  const renderPgaRows = (rows: PgaCell[][]) =>
    rows.map((row, ri) => (
      <div
        key={ri}
        className='results-card-frame'
        style={{ paddingTop: '12px', paddingBottom: '0px', alignItems: 'normal' }}
      >
        {row.map((cell, ci) => (
          <div className='tariff-inputs' key={ci}>
            <div
              className='classify-header-ticket'
              style={{ color: 'var(--Text-Secondary, var(--text-classify))' }}
            >
              {cell.label}
            </div>
            <div className='classify-cont' style={{ padding: '0px' }}>
              <div className='classify-cont-sec'>
                <div className='classify-cont-sec-div' style={cell.style}>
                  {cell.value}
                </div>
              </div>
            </div>
          </div>
        ))}
        {row.length === 1 && <div className='tariff-inputs' aria-hidden='true' />}
      </div>
    ));

  // ITAR panel data + helpers. The Claude path enriches the ITAR result with
  // several optional fields (paragraph, SME/MT flags, confidence, destination
  // policy, license note, matched snippets, review trace, missing info,
  // assumptions). Scalar/prose fields reuse renderEeiRow (wrapping, N/A
  // fallback); list fields render as wrapping bullet lists and only appear when
  // present, so older / OpenAI-path tickets render without empty rows.
  const itarData = exportWorkflowData?.itar || {};
  // NRC / DOE panel data + combined determination. The "Control Result" row shows the COMBINED
  // NRC + DOE outcome via the SAME shared helper as the live NRC card and the PDF: a DOE-only match
  // is never hidden behind a Part 110 "no clear match", and a refused / not-assessed phase reads as
  // incomplete (the old local copy string-matched a pre-Slice-3 placeholder, so a refusal could
  // show as "no match"). The Part 110 vs Part 810 specifics stay in the dedicated NRC/DOE rows.
  const nrcData = exportWorkflowData?.nrc || {};
  const nuclearControlResult = (): string =>
    combinedNuclearHeadline(exportWorkflowData) || 'N/A';

  // Policy-refusal resilience (Slice 6D) — per-phase assessment quality, read through the SAME
  // shared helper as the live cards and the PDF (utils/exportAssessment), so Saved Work agrees
  // with them. `phase_assessments` is authoritative when saved; older runs get the conservative
  // legacy inference. Everything below self-hides when every phase was assessed.
  const hasNotAssessed = notAssessedPhases(exportWorkflowData).length > 0;
  // Upfront scoped review (scope-selection Slice 5) — self-hides on full reviews / older tickets.
  const isScopedReview = Boolean(scopedReviewSummary(exportWorkflowData));
  const nrcNotAssessed = isPhaseNotAssessed(exportWorkflowData, 'nrc');
  const itarNotAssessed = isPhaseNotAssessed(exportWorkflowData, 'itar');
  // A not-assessed NRC/ITAR phase has no legal verdict — show the jurisdiction "Unresolved"
  // chip (display only), overriding any stale stored coverage_status. Same rule as the live
  // cards and the PDF.
  const nrcCoverageStatus = nrcNotAssessed
    ? 'unresolved'
    : nrcData?.coverage_status || null;
  const itarCoverageStatus = itarNotAssessed
    ? 'unresolved'
    : itarData?.coverage_status || null;
  // Full-width "Not assessed — manual review required" row directly under a section header
  // (pill while collapsed, pill + notice while expanded). Own row, never a grid cell.
  // NRC / ITAR carry their not-assessed state in the coverage row, so they only need the
  // scoped-review "not requested" row (never a duplicate not-assessed badge).
  const renderNotRequestedRow = (phase: ExportAssessmentPhase) =>
    isPhaseNotRequested(exportWorkflowData, phase) ? (
      <div style={{ padding: '0px 16px 16px' }}>
        <span className='jd-badge jd-badge--multi'>{NOT_REQUESTED_SECTION_TEXT}</span>
      </div>
    ) : null;
  const renderAssessmentRow = (phase: ExportAssessmentPhase, section: string) =>
    // Scoped review: a check the user didn't request never ran — say so under its header
    // (own full-width row, like the not-assessed one) instead of leaving bare N/A values.
    isPhaseNotRequested(exportWorkflowData, phase) ? (
      <div style={{ padding: '0px 16px 16px' }}>
        <span className='jd-badge jd-badge--multi'>{NOT_REQUESTED_SECTION_TEXT}</span>
      </div>
    ) : isPhaseOrPartNotAssessed(exportWorkflowData, phase) ? (
      // "country" = both parts of the Country and Control List Check.
      <div style={{ padding: '0px 16px 16px' }}>
        <AssessmentStatusBadge
          data={exportWorkflowData}
          phase={phase}
          variant={isSectionExpanded(section) ? 'full' : 'pill'}
        />
      </div>
    ) : null;

  const itarMatchedSnippets: string[] = Array.isArray(itarData?.matched_snippets)
    ? itarData.matched_snippets
    : [];
  const itarReviewTrace: string[] = Array.isArray(itarData?.review_trace)
    ? itarData.review_trace
    : [];
  const itarMissingInfo: string[] = Array.isArray(itarData?.missing_information)
    ? itarData.missing_information
    : [];
  const itarAssumptions: string[] = Array.isArray(itarData?.assumptions)
    ? itarData.assumptions
    : [];
  // Comparative transparency — the competing USML category/jurisdiction the ITAR agent ruled out.
  const itarClosestAlt: string = [
    itarData?.closest_alternative_category,
    itarData?.closest_alternative_reason,
  ]
    .filter(Boolean)
    .join(' — ');
  // NRC / DOE transparency (confidence / closest-alternative / assumptions) — mirrors the
  // export-page card so saved work shows HOW the determination was reached. Only rendered
  // when present, so older / OpenAI-path tickets render without empty rows.
  const capFirst = (s?: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  const nrcAssumptions: string[] = Array.isArray(nrcData?.assumptions)
    ? nrcData.assumptions
    : [];
  const nrcDoeAssumptions: string[] = Array.isArray(nrcData?.doe_assumptions)
    ? nrcData.doe_assumptions
    : [];
  // Missing information as its OWN field (parity with ITAR / CCL). Self-hides when empty.
  const nrcMissingInfo: string[] = Array.isArray(nrcData?.missing_information)
    ? nrcData.missing_information
    : [];
  const nrcDoeMissingInfo: string[] = Array.isArray(
    nrcData?.doe_missing_information,
  )
    ? nrcData.doe_missing_information
    : [];
  const nrcClosestAlt: string = [
    nrcData?.closest_alternative,
    nrcData?.closest_alternative_reason,
  ]
    .filter(Boolean)
    .join(' — ');
  const nrcDoeClosestAlt: string = [
    nrcData?.doe_closest_alternative,
    nrcData?.doe_closest_alternative_reason,
  ]
    .filter(Boolean)
    .join(' — ');
  const itarBadge = (color: string): React.CSSProperties => ({
    marginLeft: 8,
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    color: color,
    border: `1px solid ${color}`,
  });
  // A label/bullet-list column for the ITAR panel, matching the tariff-inputs
  // layout used by renderEeiRow but rendering an array as a wrapping list.
  const renderItarListCol = (label: string, items: string[]) => (
    <div className='tariff-inputs'>
      <div
        className='classify-header-ticket'
        style={{ color: 'var(--Text-Secondary, var(--text-classify))' }}
      >
        {label}
      </div>
      <div className='classify-cont' style={{ padding: '0px' }}>
        <div className='classify-cont-sec'>
          <ul
            style={{
              margin: 0,
              paddingLeft: 0,
              listStyleType: 'none',
              color: 'var(--text-primary-color)',
              fontWeight: 600,
              fontSize: '16px',
            }}
          >
            {items.map((s, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {s}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
  const itarSpacerCol = <div className='tariff-inputs' />;
  const exportScreeningPrimary =
    exportWorkflowData?.export_screening?.hs_code ||
    exportWorkflowData?.schedule_b?.hs_code ||
    exportWorkflowData?.schedule_b?.schedule_b_code ||
    cur_case.data?.commodity?.harmonizedScheduleCode ||
    cur_case.data?.commodity?.eccn ||
    '';
  const exportScreeningCountry =
    exportWorkflowData?.export_screening?.destination_country ||
    exportWorkflowData?.country_screening?.normalized_country_name ||
    exportWorkflowData?.country_screening?.input_destination_country ||
    cur_case.data?.commodity?.destinationCountry ||
    '';

  const continueImportWorkflow = () => {
    history.push({
      pathname: '/import-page',
      state: {
        resumeImportTicket: cur_case.data,
      },
    });
  };

  const continueExportWorkflow = () => {
    history.push({
      pathname: '/export-page',
      state: {
        resumeExportTicket: cur_case.data,
      },
    });
  };

  const renderClarifyingQuestions = () => (
    <>
      <h1>Clarifying Questions:</h1>
      <div className='classify-body-ticket'>
        {cur_case.data?.followUps.length > 0 ? (
          cur_case.data?.followUps.map(({ question }: any, index: number) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                paddingTop: '15px',
                width: '95%',
              }}
            >
              <div className='classify-header-ticket'>{question}</div>
              <div className='classify-cont' style={{ padding: '0px' }}>
                <div className='classify-cont-sec'>
                  <div className='classify-cont-sec-div'>
                    <input
                      placeholder='Enter details'
                      className='classify-cont-sec-input'
                      type='text'
                      disabled
                      value={responses[question] || ''}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <h3>None</h3>
        )}
      </div>
    </>
  );

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]']/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: React.ReactElement[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };

  const renderTextBlock = (label: string, value: any) => {
    if (!value) {
      return null;
    }

    return (
      <div className='bite-ticket-report-field'>
        <div className='bite-ticket-report-label'>{label}</div>
        <div className='bite-ticket-report-value'>
          {formatDisplayValue(value)}
        </div>
      </div>
    );
  };

  const renderListBlock = (label: string, values: any[]) => {
    if (!Array.isArray(values) || values.length === 0) {
      return null;
    }

    return (
      <div className='bite-ticket-report-field'>
        <div className='bite-ticket-report-label'>{label}</div>
        <ul className='bite-ticket-report-list'>
          {values.map((value, index) => (
            <li key={`${label}-${index}`}>{formatDisplayValue(value)}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderHtsChatHistory = () => {
    if (!chatHistoryMessages.length) {
      return null;
    }

    return (
      <div
        className='bite-ticket-section bite-ticket-chat-section'
        style={
          chatHistoryMaxHeight ? { maxHeight: chatHistoryMaxHeight } : undefined
        }
      >
        <div className='bite-ticket-section-header'>
          <div>
            <h2>Chat History</h2>
            <p>
              {exactChatMessages.length > 0
                ? 'Exact ChatKit thread transcript'
                : 'App-observed transcript'}
            </p>
          </div>
        </div>
        <div className='bite-ticket-chat-list'>
          {chatHistoryMessages.map((message: any, index: number) => {
            const text = getChatMessageText(message);
            if (!text) {
              return null;
            }

            return (
              <div
                key={message?.id || `hts-chat-history-${index}`}
                className={`bite-ticket-chat-message ${
                  message?.role === 'user' ? 'user' : 'assistant'
                }`}
              >
                <div className='bite-ticket-chat-role'>
                  {formatChatRole(message?.role)}
                </div>
                <div className='bite-ticket-chat-text'>{text}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderHtsFinalReport = () => {
    if (!htsFinalReport) {
      return null;
    }

    const payload = htsFinalReport.payload || {};
    const generated = htsFinalReport.generated || {};

    return (
      <div
        className='bite-ticket-section bite-ticket-report-section'
        ref={htsFinalReportRef}
      >
        <div className='bite-ticket-section-header'>
          <div>
            <h2>Final Report</h2>
            <p>
              {payload.product_desc ||
                cur_case.data.classificationValue ||
                'Saved HTS classification report'}
            </p>
          </div>
        </div>
        <div className='bite-ticket-report-grid'>
          {renderTextBlock('HTS Code', payload.hts_code || generated.hts_code)}
          {renderTextBlock(
            'Generated Commodity Description',
            generated.cmdt_desc || cur_case.data?.commodity?.commodityName,
          )}
          {renderTextBlock(
            'Summary',
            payload.summary_text || generated.summary_text,
          )}
          {renderTextBlock(
            'Legal Basis',
            payload.legal_basis_summary || generated.legal_basis_summary,
          )}
          {renderTextBlock(
            'Confidence Note',
            payload.confidence_note || generated.confidence_note,
          )}
          {renderListBlock('Reasoning Trace', payload.reasoning_trace)}
          {renderListBlock('Facts Relied On', payload.facts_relied_on)}
        </div>
      </div>
    );
  };

  const formatTariffDate = (value?: string) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return format(date, 'yyyy-MM-dd');
  };

  const formatTariffHts = (raw?: string) => {
    const digits = String(raw || '').replace(/\D/g, '');
    return [
      digits.slice(0, 4),
      digits.slice(4, 6),
      digits.slice(6, 8),
      digits.slice(8, 10),
    ]
      .filter(Boolean)
      .join('.');
  };

  const formatCountryUiLabel = (tariffResult: any) => {
    const key = String(tariffResult?.countryUi?.key || '').toUpperCase();
    const codeList = Array.isArray(tariffResult?.countryUi?.codes)
      ? tariffResult.countryUi.codes
      : [];

    const labelMap: Record<string, string> = {
      SMELTCOUNTRY: 'Country of Smelt',
      POURCOUNTRY: 'Country of Pour',
      LOADINGCOUNTRY: 'Loading Country',
    };

    const baseLabel = labelMap[key] || 'Loading Country';
    return codeList.length > 0
      ? `${baseLabel} - For ${codeList.join(', ')}`
      : baseLabel;
  };
  const formatCountryDisplay = (name?: string, iso2?: string) => {
    const cleanName = String(name || '').trim();
    const cleanIso2 = String(iso2 || '')
      .trim()
      .toUpperCase();

    if (cleanName && cleanIso2) {
      return `${cleanName} - ${cleanIso2}`;
    }

    return cleanName || cleanIso2 || '';
  };
  const formatTariffDateDisplay = (value?: string) => {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return format(date, 'MM/dd/yyyy');
  };

  const renderPdfTariffInputs = () => {
    const savedTariffInputs = cur_case?.data?.tariffResults?.inputs || {};
    const savedTariffSnapshot = cur_case?.data?.tariffResults || {};
    const loadingCountryLabel = formatCountryUiLabel(savedTariffSnapshot);

    const renderField = (
      label: string,
      value: string,
      width: string,
      opts: {
        minWidth?: string;
        withCurrency?: boolean;
      } = {},
    ) => (
      <div
        key={label}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: width,
          gap: '6px',
          minWidth: opts.minWidth,
        }}
      >
        <label
          style={{
            color: 'var(--text-primary-color)',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {label}
        </label>
        <div
          style={{
            minHeight: '45px',
            border: '1px solid var(--secondary-color)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            boxSizing: 'border-box',
            color: 'var(--text-primary-color)',
            fontSize: '16px',
            fontWeight: 500,
            gap: opts.withCurrency ? '10px' : '0px',
          }}
        >
          {opts.withCurrency && <span style={{ opacity: 0.9 }}>$</span>}
          <span>{value || '-'}</span>
        </div>
      </div>
    );

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          padding: '0 0 20px 0',
        }}
      >
        <div style={{ display: 'flex', gap: '14px', width: '100%' }}>
          {renderField(
            'HTS Code',
            formatTariffHts(savedTariffInputs.htsCode),
            '50%',
          )}
          {renderField(
            'Origin Country',
            formatCountryDisplay(
              savedTariffInputs.originCountry,
              savedTariffInputs.originIso2,
            ),
            '50%',
          )}
        </div>
        <div style={{ display: 'flex', gap: '14px', width: '100%' }}>
          {renderField(
            'Product Value',
            savedTariffInputs.productValue !== undefined
              ? `${savedTariffInputs.productValue}`
              : '',
            '40%',
            { withCurrency: true },
          )}
          {renderField(
            'Transportation Method',
            savedTariffInputs.transportMethod ||
              savedTariffSnapshot?.math?.transportMethod ||
              savedTariffSnapshot?.trans_method ||
              'Ocean',
            '20%',
          )}
          {renderField(
            'Entry Date',
            formatTariffDateDisplay(
              savedTariffInputs.effectDate || savedTariffInputs.entryDate,
            ),
            '20%',
          )}
          {renderField(
            'Loading Date',
            formatTariffDateDisplay(
              savedTariffInputs.loadDate || savedTariffInputs.loadingDate,
            ),
            '20%',
          )}
        </div>
        <div
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--text-primary-color)',
            marginTop: '4px',
          }}
        >
          Additional Information:
        </div>
        {!!savedTariffSnapshot?.countryUi?.show && (
          <div style={{ display: 'flex', gap: '14px', width: '100%' }}>
            {renderField(
              loadingCountryLabel,
              formatCountryDisplay(
                savedTariffInputs.loadingCountry,
                savedTariffInputs.loadingCountryIso2,
              ) ||
                savedTariffInputs.loadingCountry ||
                '',
              '40%',
            )}
          </div>
        )}
      </div>
    );
  };
  const renderPdfTariffInsights = () => {
    const savedTariffSnapshot = cur_case?.data?.tariffResults || {};
    const riskFlags = savedTariffSnapshot?.insights?.riskFlags || [];
    const htsHistory = savedTariffSnapshot?.insights?.history || [];

    const renderCard = (title: string, content: React.ReactNode) => (
      <div
        style={{
          borderTop: '2px solid var(--bg-secondary)',
          paddingTop: '18px',
          marginTop: '18px',
        }}
      >
        <div
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--text-primary-color)',
            marginBottom: '12px',
          }}
        >
          {title}
        </div>
        {content}
      </div>
    );

    return (
      <div style={{ width: '100%', display: 'block', marginBottom: '24px' }}>
        {renderCard(
          'Exclusion Applicability Assistant',
          <div style={{ color: 'var(--text-primary-color)', fontSize: '14px' }}>
            No exclusions applicable for assistance found for this transaction.
          </div>,
        )}

        {renderCard(
          'Risk Flags',
          riskFlags.length > 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              {riskFlags.map((flag: any, index: number) => (
                <div
                  key={flag.id || index}
                  style={{
                    border: `1px solid ${
                      flag.severity === 'high'
                        ? '#F2B8B5'
                        : flag.severity === 'medium'
                        ? '#F3E2A9'
                        : '#D4E3FF'
                    }`,
                    borderRadius: '10px',
                    padding: '14px 16px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--text-primary-color)',
                      marginBottom: '8px',
                    }}
                  >
                    {flag.title}
                  </div>
                  <div
                    style={{
                      color: 'var(--text-primary-color)',
                      fontSize: '14px',
                    }}
                  >
                    {flag.description}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{ color: 'var(--text-primary-color)', fontSize: '14px' }}
            >
              No flags found for AD/CVD, Forced Labor, or PGA.
            </div>
          ),
        )}

        {renderCard(
          'HTS Code History',
          htsHistory.length > 0 ? (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              {htsHistory.map((item: any, index: number) => (
                <div key={item.id || index}>
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: 'var(--text-primary-color)',
                      marginBottom: '4px',
                    }}
                  >
                    {item.date}
                  </div>
                  <div
                    style={{
                      color: 'var(--text-primary-color)',
                      fontSize: '14px',
                    }}
                  >
                    {item.summary}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{ color: 'var(--text-primary-color)', fontSize: '14px' }}
            >
              No recent changes detected for this HTS code.
            </div>
          ),
        )}
      </div>
    );
  };
  const MPF_RATE = 0.003464;

  const calcMpf = (
    productValueLocal: number,
    entryDate: Date | null | undefined,
  ): number => {
    const value = Number(productValueLocal) || 0;
    if (!value) {
      return 0;
    }

    const raw = value * MPF_RATE;

    // Match TariffPage defaults exactly.
    let min = 33.58;
    let max = 651.5;

    if (entryDate instanceof Date && !Number.isNaN(entryDate.getTime())) {
      const time = entryDate.getTime();

      if (time >= new Date('2025-10-01T00:00:00').getTime()) {
        min = 33.58;
        max = 651.5;
      } else if (time >= new Date('2024-10-01T00:00:00').getTime()) {
        min = 32.71;
        max = 634.62;
      } else if (time >= new Date('2023-10-01T00:00:00').getTime()) {
        min = 31.67;
        max = 614.35;
      } else if (time >= new Date('2022-10-01T00:00:00').getTime()) {
        min = 29.66;
        max = 575.35;
      } else {
        min = 27.75;
        max = 538.4;
      }
    }

    return Math.min(Math.max(raw, min), max);
  };

  // A ticket saved before the engine reported fees stored a total without MPF
  // or HMF, and this view has always added them for display. New tickets
  // store the engine's fees and total, which are shown as saved.
  const legacyTotalWithFees = (
    snap: any,
    productValue: number,
    dutyCostValue: number,
  ): number => {
    const entryDateValue = snap?.inputs?.effectDate || snap?.inputs?.entryDate;
    const effectDate = entryDateValue ? new Date(entryDateValue) : new Date();
    const enabledFtasMap = normalizeEnabledExclusions(
      snap?.results?.enabledFtas,
    );
    const cleanHts = String(snap?.inputs?.htsCode || '').replace(/\D/g, '');
    const transportMethod = String(
      snap?.inputs?.transportMethod ||
        snap?.inputs?.transport_method ||
        snap?.trans_method ||
        'Ocean',
    ).toLowerCase();

    const isChapter98Exempt =
      cleanHts.startsWith('9801') ||
      cleanHts.startsWith('9804') ||
      cleanHts.startsWith('9805') ||
      cleanHts.startsWith('9813');

    // Origin-based MPF exemptions, checked before the FTA list because that
    // list gates every other programme on the preference actually being
    // claimed. These three turn on origin alone — no SPI required:
    // products of Israel (19 CFR 24.23(c)(5)), of CBERA beneficiary countries
    // (General Note 7, HTSUS), and of least-developed beneficiary developing
    // countries (General Note 4(b)(i), HTSUS).
    const isOriginExempt = isOriginMpfExempt(
      snap?.inputs?.originIso2,
      snap?.inputs?.originCountry,
      effectDate,
    );

    const exemptFtas = [
      'AU',
      'BH',
      'CL',
      'P',
      'P+',
      'CO',
      'IL',
      'KR',
      'OM',
      'PA',
      'PE',
      'SC',
      'S',
      'S+',
      'E',
      'R',
      'Y',
    ];

    const isFtaExempt = exemptFtas.some(
      (code) => enabledFtasMap[code] === true,
    );
    const mpfValue =
      isChapter98Exempt || isOriginExempt || isFtaExempt
        ? 0
        : calcMpf(productValue, effectDate);
    const hmfValue = transportMethod === 'ocean' ? productValue * 0.00125 : 0;
    return productValue + dutyCostValue + mpfValue + hmfValue;
  };

  const formatYesNo = (value?: boolean | null) =>
    value === true ? 'Yes' : value === false ? 'No' : 'N/A';

  useEffect(() => {
    const snap = cur_case.data.tariffResults; // new snapshot shape
    const built = buildTicketTariffProps(snap);
    if (!built) {
      return;
    }
    const isSnapshot =
      built.trForComponent &&
      typeof built.trForComponent === 'object' &&
      built.trForComponent.results;

    // Base duty in dollars, backed out of the saved total rather than
    // re-derived from the rate string.
    const baseDutyCost = isSnapshot
      ? engineFigures(cur_case.data.tariffResults?.math).baseDuty
      : 0;

    const additionalRateDisplay: string =
      built.extra.displayAdditionalRate ??
      (isSnapshot
        ? built.trForComponent.results.math?.additionalDutyRateDisplay
        : built.trForComponent.results.additionalDutyRate) ??
      '—';

    const productValue = Number(snap?.inputs?.productValue || 0);
    const dutyCostValue = Number(
      snap?.math?.dutyCost ?? built.extra.dutyCost ?? 0,
    );
    const totalCostValue =
      typeof snap?.math?.mpf === 'number' &&
      typeof snap?.math?.totalCost === 'number'
        ? snap.math.totalCost
        : legacyTotalWithFees(snap, productValue, dutyCostValue);

    setTotalCost(
      totalCostValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );
    setTotalAddCost(additionalRateDisplay);
    setTotalBaseCost(
      baseDutyCost.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );
  }, [cur_case.data.tariffResults]);

  const earCcl = cur_case.data?.workflowData[0]?.ear_ccl;

  // EAR/CCL transparency (confidence / considered / closest-alternative / assumptions) — mirrors
  // the export-page card so saved work shows HOW the ECCN determination was reached.
  const earCclConfidence: string = earCcl?.confidence
    ? String(earCcl.confidence).charAt(0).toUpperCase() +
      String(earCcl.confidence).slice(1)
    : '';
  const earCclCandidates: string = Array.isArray(earCcl?.candidate_eccns)
    ? earCcl.candidate_eccns.join(', ')
    : '';
  const earCclClosestAlt: string = [
    earCcl?.closest_alternative_eccn,
    earCcl?.closest_alternative_reason,
  ]
    .filter(Boolean)
    .join(' — ');
  const earCclAssumptions: string[] = Array.isArray(earCcl?.assumptions)
    ? earCcl.assumptions
    : [];

  const scopeAndOrderReview = earCcl?.scope_and_order_review;
  const review9x515600 =
    earCcl?.review_9x515_600 || earCcl?.nine_x_515_or_600_series_review;
  const speciallyDesignedReview = earCcl?.specially_designed_analysis;
  const candidateEccnReview = earCcl?.candidate_eccn_review;
  const licenseReviewSummary = earCcl?.license_review_summary;
  const endUseEndUserReviewSummary = earCcl?.end_use_end_user_review_summary;

  const cclStepLabels: Record<string, string> = {
    'ccl.scope_and_order': 'EAR scope and order of review',
    'ccl.9x515_600': '9x515 / 600-series review',
    'ccl.specially_designed': 'Specially designed review',
    'ccl.candidate_eccn': 'Recommended ECCN Review',
    'ccl.license_review': 'License review',
    'ccl.end_use_end_user': 'End-use / end-user review',
    'ccl.finalize': 'Finalizing EAR / CCL result',
  };

  const toYesNo = (value?: boolean | null) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'N/A';
  };

  const joinList = (items?: string[]) => {
    if (!items || !items.length) return 'N/A';
    return items.join(', ');
  };

  const joinParts = (parts: Array<string | null | undefined>) => {
    return parts.filter(Boolean).join(' ');
  };

  const scopeAndOrderText = joinParts([
    scopeAndOrderReview?.subject_to_ear_status
      ? `Subject to EAR status: ${scopeAndOrderReview.subject_to_ear_status}.`
      : null,
    scopeAndOrderReview?.subject_to_ear_basis
      ? `Basis: ${scopeAndOrderReview.subject_to_ear_basis}`
      : null,
    scopeAndOrderReview?.likely_categories?.length
      ? `Likely categories: ${joinList(scopeAndOrderReview.likely_categories)}.`
      : null,
    scopeAndOrderReview?.likely_product_groups?.length
      ? `Likely product groups: ${joinList(
          scopeAndOrderReview.likely_product_groups,
        )}.`
      : null,
    scopeAndOrderReview?.encryption_cross_cut_required !== undefined
      ? `Encryption cross-cut required: ${toYesNo(
          scopeAndOrderReview.encryption_cross_cut_required,
        )}.`
      : null,
  ]);

  const review9x515600Text = review9x515600
    ? joinParts([
        review9x515600?.review_required !== undefined
          ? `Review required: ${toYesNo(review9x515600.review_required)}.`
          : null,
        review9x515600?.likely_trumping_eccns?.length
          ? `Likely trumping ECCNs: ${joinList(
              review9x515600.likely_trumping_eccns,
            )}.`
          : null,
        review9x515600?.reasoning
          ? `Reasoning: ${review9x515600.reasoning}`
          : null,
        review9x515600?.specially_designed_relevant !== undefined
          ? `Specially designed relevant: ${toYesNo(
              review9x515600.specially_designed_relevant,
            )}.`
          : null,
      ])
    : '';

  const speciallyDesignedText = speciallyDesignedReview
    ? joinParts([
        speciallyDesignedReview?.specially_designed_relevant !== undefined
          ? `Relevant: ${toYesNo(
              speciallyDesignedReview.specially_designed_relevant,
            )}.`
          : null,
        speciallyDesignedReview?.catch_analysis
          ? `Catch analysis: ${speciallyDesignedReview.catch_analysis}`
          : null,
        speciallyDesignedReview?.release_analysis
          ? `Release analysis: ${speciallyDesignedReview.release_analysis}`
          : null,
        speciallyDesignedReview?.development_documentation_needed !== undefined
          ? `Development documentation needed: ${toYesNo(
              speciallyDesignedReview.development_documentation_needed,
            )}.`
          : null,
        speciallyDesignedReview?.missing_technical_details?.length
          ? `Missing technical details: ${joinList(
              speciallyDesignedReview.missing_technical_details,
            )}.`
          : null,
      ])
    : '';

  const candidateEccnText = joinParts([
    candidateEccnReview?.selected_eccn || earCcl?.eccn
      ? `Selected ECCN: ${candidateEccnReview?.selected_eccn || earCcl?.eccn}${
          candidateEccnReview?.selected_eccn_paragraph || earCcl?.eccn_paragraph
            ? ` (${
                candidateEccnReview?.selected_eccn_paragraph ||
                earCcl?.eccn_paragraph
              })`
            : ''
        }.`
      : null,
    candidateEccnReview?.confidence || earCcl?.confidence
      ? `Confidence: ${candidateEccnReview?.confidence || earCcl?.confidence}.`
      : null,
    candidateEccnReview?.candidate_eccns?.length
      ? `Candidate ECCNs: ${joinList(candidateEccnReview.candidate_eccns)}.`
      : null,
    candidateEccnReview?.reasoning
      ? `Reasoning: ${candidateEccnReview.reasoning}`
      : null,
    candidateEccnReview?.matched_snippets?.length ||
    earCcl?.matched_snippets?.length
      ? `Matched snippet: ${
          candidateEccnReview?.matched_snippets?.[0] ||
          earCcl?.matched_snippets?.[0]
        }`
      : null,
    // "Missing information" is now its OWN row (cclMissingInfoText below) — parity with ITAR/NRC.
  ]);

  // Missing information — its own dedicated CCL field (parity with ITAR / NRC). Self-hides when empty.
  const cclMissingInfoText = joinList(
    candidateEccnReview?.missing_technical_details ||
      earCcl?.missing_technical_details,
  );

  const licenseReviewText = licenseReviewSummary
    ? joinParts([
        licenseReviewSummary?.reasons_for_control?.length
          ? `Reasons for control: ${joinList(
              licenseReviewSummary.reasons_for_control,
            )}.`
          : null,
        licenseReviewSummary?.country_chart_columns?.length
          ? `Country chart columns: ${joinList(
              licenseReviewSummary.country_chart_columns,
            )}.`
          : null,
        licenseReviewSummary?.license_required !== undefined
          ? `License required: ${toYesNo(
              licenseReviewSummary.license_required,
            )}.`
          : null,
        licenseReviewSummary?.license_required_basis?.length
          ? `License basis: ${joinList(
              licenseReviewSummary.license_required_basis,
            )}.`
          : null,
        licenseReviewSummary?.license_exception_notes?.length
          ? `License exception notes: ${joinList(
              licenseReviewSummary.license_exception_notes,
            )}.`
          : null,
        licenseReviewSummary?.embargo_or_special_controls?.length
          ? `Embargo or special controls: ${joinList(
              licenseReviewSummary.embargo_or_special_controls,
            )}.`
          : null,
      ])
    : joinParts([
        earCcl?.reasons_for_control?.length
          ? `Reasons for control: ${joinList(earCcl.reasons_for_control)}.`
          : null,
        earCcl?.country_chart_columns?.length
          ? `Country chart columns: ${joinList(earCcl.country_chart_columns)}.`
          : null,
        earCcl?.license_required !== undefined
          ? `License required: ${toYesNo(earCcl.license_required)}.`
          : null,
        earCcl?.license_required_basis?.length
          ? `License basis: ${joinList(earCcl.license_required_basis)}.`
          : null,
        earCcl?.embargo_or_special_controls?.length
          ? `Embargo or special controls: ${joinList(
              earCcl.embargo_or_special_controls,
            )}.`
          : null,
      ]);

  // CHIPS §744.23 — advanced-computing / SME end-use heads-up (self-hides; only such items carry it).
  const advancedComputingNote =
    endUseEndUserReviewSummary?.advanced_computing_note ||
    earCcl?.advanced_computing_note;
  const endUseEndUserText = endUseEndUserReviewSummary
    ? joinParts([
        endUseEndUserReviewSummary?.part_744_review?.length
          ? `Part 744 review: ${joinList(
              endUseEndUserReviewSummary.part_744_review,
            )}.`
          : null,
        advancedComputingNote
          ? `Advanced-computing (§744.23): ${advancedComputingNote}`
          : null,
        endUseEndUserReviewSummary?.red_flag_notes?.length
          ? `Red flag notes: ${joinList(
              endUseEndUserReviewSummary.red_flag_notes,
            )}.`
          : null,
      ])
    : joinParts([
        earCcl?.part_744_review?.length
          ? `Part 744 review: ${joinList(earCcl.part_744_review)}.`
          : null,
        advancedComputingNote
          ? `Advanced-computing (§744.23): ${advancedComputingNote}`
          : null,
        earCcl?.red_flag_notes?.length
          ? `Red flag notes: ${joinList(earCcl.red_flag_notes)}.`
          : null,
      ]);

  // Slice 4 — international / multilateral overlay (EU dual-use + originating regimes). Supplementary
  // screening signal; the saved-work section only renders when this is non-empty (older tickets have none).
  const multilateralText = joinParts([
    earCcl?.regime_basis?.length
      ? `Regime basis: ${joinList(earCcl.regime_basis)}.`
      : null,
    earCcl?.eu_dual_use_entry
      ? `EU dual-use list: ${earCcl.eu_dual_use_entry}`
      : null,
    // Task #5 — deterministic foreign-control correlation (EU / UK / Canada). One line per
    // jurisdiction; low-confidence rows show the "no direct equivalent" advisory (the not-1:1 case).
    earCcl?.foreign_equivalents?.length
      ? `International equivalents (indicative — verify with each destination's authority):\n${earCcl.foreign_equivalents
          .map(
            (fe: any) =>
              `  • ${fe?.jurisdiction || ''}: ${
                fe?.confidence === 'low'
                  ? 'no direct equivalent — verify locally'
                  : `${fe?.control_number || ''}${fe?.regime ? ` (${fe.regime})` : ''}`
              }`,
          )
          .join('\n')}`
      : null,
    earCcl?.multilateral_notes || null,
  ]);

  // ── Lower CCL detail cells: End-use / International-Multilateral / BIS guidance. ──────────────
  // Laid out TWO-PER-ROW below (results-card-frame + two flex:1 tariff-inputs) so they align with
  // the paired sections above (Recommended ECCN + License review) instead of stacking full-width in
  // a single left column. End-use always shows (N/A fallback, like the rows above); Multilateral and
  // BIS self-hide. Values use maxWidth:100% because each now sits in its own 50% column.
  const cclLowerCellBaseStyle: React.CSSProperties = {
    display: 'block',
    maxWidth: '100%',
    color: 'var(--text-primary-color)',
    fontWeight: '600',
    fontSize: '16px',
  };
  // License Exceptions (EAR Part 740) — formatted once, reused as its own 2-col cell below.
  const cclLicenseExceptionsText = formatLicenseExceptions(earCcl?.license_exceptions);
  const cclLowerCells: Array<{
    label: string;
    value: string;
    style: React.CSSProperties;
  }> = [
    {
      label: 'End-use / end-user review:',
      value: endUseEndUserText || 'N/A',
      style: {
        ...cclLowerCellBaseStyle,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    ...(multilateralText
      ? [
          {
            label: 'International / Multilateral:',
            value: multilateralText,
            // pre-line keeps the per-jurisdiction international-equivalents lines on separate rows.
            style: { ...cclLowerCellBaseStyle, whiteSpace: 'pre-line' },
          },
        ]
      : []),
    // License Exception ENC (§740.17) — deterministic self-classification-report-vs-CCATS pathway for
    // Category 5 Part 2 encryption ECCNs (5A002 / 5A992.c alike). Self-hides on non-encryption items.
    ...(earCcl?.enc_pathway
      ? [
          {
            label: 'License Exception ENC (§740.17):',
            value: String(earCcl.enc_pathway),
            // Full paragraph — pre-line wrap, no ellipsis, so the pathway is never truncated.
            style: { ...cclLowerCellBaseStyle, whiteSpace: 'pre-line' },
          },
        ]
      : []),
    // License Exceptions (EAR Part 740) — deterministic grounded determination as its OWN cell
    // (headline + candidate exceptions + the reason none apply). Self-hides when empty. pre-line so
    // the per-exception bullet lines stay on separate rows.
    ...(cclLicenseExceptionsText
      ? [
          {
            label: 'License Exceptions (15 CFR Part 740):',
            value: cclLicenseExceptionsText,
            style: { ...cclLowerCellBaseStyle, whiteSpace: 'pre-line' },
          },
        ]
      : []),
    ...(earCcl?.bis_guidance_notes
      ? [
          {
            label: 'BIS guidance:',
            value: String(earCcl.bis_guidance_notes),
            style: {
              ...cclLowerCellBaseStyle,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
        ]
      : []),
  ];
  // Two columns per results-card-frame row (matches the paired sections above); a lone trailing
  // cell keeps the left column via a spacer so the 2-column alignment holds.
  const cclLowerRows: Array<typeof cclLowerCells> = [];
  for (let i = 0; i < cclLowerCells.length; i += 2) {
    cclLowerRows.push(cclLowerCells.slice(i, i + 2));
  }

  return (
    <>
      {/* <div
        className='results-card-frame'
        style={{ paddingBottom: '0px', gap: '12px' }}
      >
        <button className='btn-new-automate' onClick={updateTariff}>
          <div>Check Current Tariff</div>
        </button>

        <button className='btn-new-automate' onClick={updateScreening}>
          <div>Update Screening Results</div>
        </button>
      </div> */}
      <div ref={isImportUnified || isExportUnified ? unifiedPdfRef : undefined}>
        {(isImportUnified || isExportUnified) && (
          <div
            className='results-card-frame'
            style={{ paddingBottom: '0px', gap: '12px' }}
          >
            <button
              className='btn-new-automate'
              onClick={
                isExportUnified
                  ? continueExportWorkflow
                  : continueImportWorkflow
              }
            >
              <div>Continue Workflow</div>
            </button>
          </div>
        )}
        <div className='results-card-frame'>
          <div className='results-card-frame-comp'>
            <div className='results-card-frame-head'>Last Screen</div>
            <div className='results-card-frame-body'>
              {cur_case.data?.lastUpdate
                ? formatDate(cur_case.data?.lastUpdate)
                : formatDate(cur_case.data?.createdAt.substring(0, 10))}
            </div>
            <div className='results-card-frame-footer'></div>
          </div>
          <div className='results-card-frame-comp'>
            <div className='results-card-frame-head'>Screening Frequency</div>
            <div className='results-card-frame-body'>
              {isReScreenableDocType(cur_case.data?.docType)
                ? cur_case.data?.frequency
                : 'N/A'}
            </div>
            <div className='results-card-frame-footer'></div>
          </div>
          <div className='results-card-frame-comp'>
            <div className='results-card-frame-head'>Status</div>
            <div className='results-card-frame-body'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='9'
                height='8'
                viewBox='0 0 9 8'
                fill='none'
                style={{ marginRight: '5px' }}
              >
                <circle
                  cx='4.5'
                  cy='4'
                  r='3'
                  fill={
                    cur_case.data.status === 'Complete'
                      ? 'var(--text-classify)'
                      : cur_case.data.status === 'Pending'
                      ? '#FDB022'
                      : cur_case.data.status === 'New Results'
                      ? '#4895EF'
                      : cur_case.data.status === 'Approved'
                      ? '#47CD89'
                      : cur_case.data.status === 'Rejected'
                      ? '#F04438'
                      : '#B67DFF'
                  }
                />
              </svg>
              {cur_case.data.status}
            </div>
            <div
              className='results-card-frame-footer'
              onClick={() => setIsApprovalModalOpen(true)}
            >
              Change
            </div>
          </div>
          <div className='results-card-frame-comp'>
            <div className='results-card-frame-head'>Ticket Type</div>
            <div className='results-card-frame-body-doc'>
              <div className='results-card-frame-body-texts'>
                {cur_case.data.docType}
              </div>
            </div>
            <div
              className='results-card-frame-footer'
              style={{ height: '18px' }}
            >
              {' '}
            </div>
          </div>
        </div>
        <div
          className='results-card-frame'
          style={{
            paddingTop: '12px',
            paddingBottom: '0px',
            flexDirection: 'column',
          }}
        >
          {/* Classification UI */}
          <div
            className='results-card-frame-comp'
            style={{
              width: '100%',
              flexDirection: 'column',
              marginBottom: '24px',
            }}
          >
            <div
              className='entries-frame-body-cont-lenses'
              style={{ padding: '0px' }}
            >
              <div
                className='entries-frame-body-cont-lenses-head'
                onClick={() => {
                  if (activeFilters === 'Classification') {
                    setOpenFilter(!openFilter);
                    setActiveFilters('');
                  } else {
                    setOpenFilter(true);
                    setActiveFilters('Classification');
                  }
                }}
              >
                <LocalOfferOutlinedIcon
                  sx={{ fontSize: 28, color: '#4895EF' }}
                />
                <div className={'entries-frame-body-cont-lenses-head-text'}>
                  {['Export Unified'].includes(cur_case.data.docType)
                    ? 'Chat Summary'
                    : 'Classification'}
                </div>
                {openFilter && activeFilters === 'Classification' ? (
                  <KeyboardArrowUpOutlinedIcon
                    sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                  />
                ) : (
                  <KeyboardArrowDownOutlinedIcon
                    sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                  />
                )}
              </div>
            </div>
            {isSectionCollapsed('Classification') && (
              <>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    borderTop: '2px solid rgb(46, 48, 54)',
                  }}
                ></div>
                <div
                  className='results-card-frame'
                  style={{ paddingTop: '12px', paddingBottom: '0px' }}
                >
                  <div className='tariff-inputs'>
                    <div
                      className='classify-header-ticket'
                      style={{
                        color:
                          'var(--Text-Secondary, var(--text-classify)ext-classify))',
                      }}
                    >
                      {'Product Description:'}
                    </div>
                    <div className='classify-cont' style={{ padding: '0px' }}>
                      <div className='classify-cont-sec'>
                        <div
                          className='classify-cont-sec-div'
                          style={{
                            display: 'block',
                            width: '449px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            maxWidth: '100%',
                            textOverflow: 'ellipsis',
                            color: 'var(--text-primary-color)',
                            fontWeight: '600',
                            fontSize: '16px',
                          }}
                        >
                          {cur_case.data?.commodity?.commodityName}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tariff-inputs'>
                    <div
                      className='classify-header-ticket'
                      style={{
                        color: 'var(--Text-Secondary, var(--text-classify))',
                      }}
                    >
                      {['Export Unified'].includes(cur_case.data.docType)
                        ? 'Schedule B Code'
                        : 'HTS Code'}
                    </div>
                    <div className='classify-cont' style={{ padding: '0px' }}>
                      <div className='classify-cont-sec'>
                        <div
                          className='classify-cont-sec-div'
                          style={{
                            color: 'var(--text-primary-color)',
                            fontWeight: '600',
                            fontSize: '16px',
                          }}
                        >
                          {cur_case.data?.commodity?.harmonizedScheduleCode}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {isSectionExpanded('Classification') &&
              (isExportUnified ||
                cur_case.data?.aiStream.length > 0 ||
                Boolean(htsFinalReport) ||
                chatHistoryMessages.length > 0) && (
                <>
                  <div
                    className='results-frame-col'
                    ref={componentRef}
                    style={{ border: '0px' }}
                  >
                    {isImportUnified && !isLegacyClassificationTicket ? (
                      <>
                        <div className='bite-class-ticket-summary'>
                          <div className='bite-class-ticket-summary-head'>
                            <div>
                              <div className='bite-class-ticket-eyebrow'>
                                Saved Classification
                              </div>
                              <h2>
                                {cur_case.data?.commodity?.commodityName ||
                                  cur_case.data?.classificationValue ||
                                  'Classification result'}
                              </h2>
                            </div>
                            {cur_case.data?.commodity
                              ?.harmonizedScheduleCode && (
                              <div className='bite-class-ticket-code'>
                                <span>HTS Code</span>
                                <strong>
                                  {
                                    cur_case.data.commodity
                                      .harmonizedScheduleCode
                                  }
                                </strong>
                              </div>
                            )}
                          </div>

                          {cur_case.data?.followUps?.length > 0 && (
                            <div className='bite-class-ticket-followups'>
                              <div className='bite-class-ticket-followups-title'>
                                Clarifying Questions
                              </div>
                              <div className='bite-class-ticket-followups-list'>
                                {cur_case.data.followUps.map(
                                  ({ question }: any, index: number) => (
                                    <div
                                      className='bite-class-ticket-followup'
                                      key={`${question}-${index}`}
                                    >
                                      <span>{question}</span>
                                      <strong>
                                        {responses[question] ||
                                          'No answer saved'}
                                      </strong>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {!htsFinalReport && (
                            <div className='bite-class-ticket-stream'>
                              <StreamDisplay
                                streamData={cur_case.data?.aiStream}
                                ticket={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className='bite-class-ticket-main-row'>
                          {renderHtsChatHistory()}
                          {renderHtsFinalReport()}
                        </div>
                      </>
                    ) : (
                      <div
                        className='results-frame-classify'
                        style={{ marginBottom: '0px' }}
                      >
                        <div className='class-ticket-container'>
                          <div className='class-ticket-left-div'>
                            <div className='class-ticket-searched-val'>
                              <h1>Classification Description:</h1>
                              <h2>
                                {isExportUnified
                                  ? exportDescription || 'N/A'
                                  : cur_case.data?.classificationValue}
                              </h2>
                            </div>
                            {cur_case.data.docType === 'Import Unified' && (
                              <div className='class-ticket-searched-val'>
                                <h1>Optional Details:</h1>
                                <h2>
                                  Material:{' '}
                                  {cur_case.data.commodity.material || 'N/A'}
                                </h2>
                                <h2>
                                  Intended Use:{' '}
                                  {cur_case.data.commodity.intendedUse ||
                                    'N/A'}
                                </h2>
                                <h2>
                                  Physical State:{' '}
                                  {cur_case.data.commodity.physicalState ||
                                    'N/A'}
                                </h2>
                              </div>
                            )}

                            <div className='class-ticket-results'>
                              <h1>Results:</h1>
                              <h2>
                                Product Description:{' '}
                                {cur_case.data?.commodity?.commodityName}
                              </h2>
                              <h2>
                                {['Export Unified'].includes(
                                  cur_case.data.docType,
                                )
                                  ? 'Schedule B Code'
                                  : 'HTS Code'}
                                {
                                  cur_case.data?.commodity
                                    ?.harmonizedScheduleCode
                                }
                              </h2>
                            </div>
                            {!isExportUnified && renderClarifyingQuestions()}
                          </div>
                          <div style={{ width: '70%' }}>
                            {isImportUnified ? (
                              <div className='classify-results-card-list'>
                                <StreamDisplay
                                  streamData={cur_case.data?.aiStream}
                                  ticket={true}
                                />
                              </div>
                            ) : (
                              <div
                                className='class-ticket-left-div'
                                style={{ width: '100%' }}
                              >
                                {renderClarifyingQuestions()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {isImportUnified && (
                      <div
                        className={
                          isLegacyClassificationTicket
                            ? 'api-container'
                            : 'bite-class-ticket-reference'
                        }
                        style={
                          isLegacyClassificationTicket
                            ? { gap: '24px', padding: '0px 20px 15px 25px' }
                            : undefined
                        }
                      >
                        <div
                          className='api-container-left'
                          style={
                            isLegacyClassificationTicket
                              ? { flex: '1' }
                              : undefined
                          }
                        >
                          <h1 style={{ fontSize: '14px' }}>CROSS Rulings</h1>
                          {hasOnlyUrlCrossRulings ? (
                            <p
                              className={
                                !isExpandedCross
                                  ? 'class-ticket-cross-expandeds'
                                  : ''
                              }
                            >
                              {crossRulingLinks.map((item, index) => (
                                <React.Fragment key={`${item.url}-${index}`}>
                                  <a
                                    href={item.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='class-ticket-cross-rules'
                                  >
                                    {item.url}
                                  </a>
                                  <br />
                                </React.Fragment>
                              ))}
                            </p>
                          ) : crossRulingLinks.length > 0 ? (
                            <div
                              className='bite-classify-cross-links'
                              style={
                                isLegacyClassificationTicket
                                  ? undefined
                                  : { maxHeight: '220px' }
                              }
                            >
                              {visibleCrossRulingLinks.map((item, index) => (
                                <div
                                  key={`${item.url}-${index}`}
                                  className='bite-classify-cross-link-card'
                                >
                                  <a
                                    href={item.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='bite-classify-cross-link-title'
                                  >
                                    {item.title || item.url}
                                  </a>

                                  {typeof item.relevance_percent ===
                                    'number' && (
                                    <div className='bite-classify-cross-link-relevance'>
                                      Relevance: {item.relevance_percent}%
                                    </div>
                                  )}

                                  {item.relevance_note && (
                                    <div className='bite-classify-cross-link-note'>
                                      {item.relevance_note}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className='bite-classify-path-awaiting'>
                              Awaiting information...
                            </p>
                          )}
                          {crossRulingLinks.length > 5 && (
                            <div
                              className='expl-notes-btn'
                              onClick={() =>
                                setIsExpandedCross(!isExpandedCross)
                              }
                            >
                              {!isExpandedCross ? 'Read More' : 'Read Less'}
                            </div>
                          )}
                        </div>

                        <div
                          className='api-container-left'
                          style={
                            isLegacyClassificationTicket
                              ? { flex: '2.2' }
                              : undefined
                          }
                        >
                          {/* <h1 style={{ fontSize: '14px' }}>Explanatory Notes</h1>
                          {cur_case.data.exNotes && (
                            <div
                              className={`explanatory_notes-classify ${!isExpanded ? 'expanded' : ''
                                }`}
                            >
                              {formatExplanatoryNote(cur_case.data.exNotes)}
                            </div>
                          )}
                          {cur_case.data.exNotes && (
                            <div
                              className='expl-notes-btn-classify'
                              onClick={() => setIsExpanded(!isExpanded)}
                            >
                              {!isExpanded ? 'Read More' : 'Read Less'}
                            </div>
                          )} */}
                          <h1 style={{ fontSize: '14px', paddingTop: '0px' }}>
                            Section and Chapter Notes
                          </h1>
                          {cur_case.data.chapterNotes && (
                            <div
                              className={`explanatory_notes-classify ${
                                !isExpandedSecNotes ? 'expanded' : ''
                              }`}
                            >
                              {formatExplanatoryNote(
                                cur_case.data.chapterNotes,
                              )}
                            </div>
                          )}
                          {cur_case.data.chapterNotes && (
                            <div
                              className='expl-notes-btn-classify'
                              onClick={() =>
                                setIsExpandedSecNotes(!isExpandedSecNotes)
                              }
                            >
                              {!isExpandedSecNotes ? 'Read More' : 'Read Less'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
          </div>

          {/* Export Country Control UI */}
          {['Export Unified'].includes(cur_case.data.docType) && (
            <>
              {/* Manual Review Required — every phase whose automated assessment could not be
                  completed (policy-refusal resilience, Slice 6D). FIRST: no overall clearance may be
                  read from the sections below while it stands. Same collapsible shell as the other
                  sections (pill collapsed, full notice expanded). Self-hides when all were assessed. */}
              {isExportUnified && hasNotAssessed && (
                <div
                  className='results-card-frame-comp'
                  style={{
                    width: '100%',
                    flexDirection: 'column',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    className='entries-frame-body-cont-lenses'
                    style={{ padding: '0px' }}
                  >
                    <div
                      className='entries-frame-body-cont-lenses-head'
                      onClick={() => {
                        if (activeFilters === 'ManualReview') {
                          setOpenFilter(!openFilter);
                          setActiveFilters('');
                        } else {
                          setOpenFilter(true);
                          setActiveFilters('ManualReview');
                        }
                      }}
                    >
                      <ReportProblemOutlinedIcon
                        sx={{ fontSize: 28, color: '#f79009' }}
                      />
                      <div className='entries-frame-body-cont-lenses-head-text'>
                        Manual Review Required
                      </div>
                      {openFilter && activeFilters === 'ManualReview' ? (
                        <KeyboardArrowUpOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      ) : (
                        <KeyboardArrowDownOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      borderTop: '2px solid rgb(46, 48, 54)',
                    }}
                  />
                  <div style={{ padding: '16px' }}>
                    <NotAssessedSummary
                      data={exportWorkflowData}
                      variant={isSectionExpanded('ManualReview') ? 'full' : 'pill'}
                    />
                  </div>
                </div>
              )}
              {/* Scoped Review — the run covered only the checks picked in the upfront scope popup
                  (scope-selection Slice 5). Same collapsible shell as Manual Review; self-hides on
                  a full review / older tickets. */}
              {isExportUnified && isScopedReview && (
                <div
                  className='results-card-frame-comp'
                  style={{
                    width: '100%',
                    flexDirection: 'column',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    className='entries-frame-body-cont-lenses'
                    style={{ padding: '0px' }}
                  >
                    <div
                      className='entries-frame-body-cont-lenses-head'
                      onClick={() => {
                        if (activeFilters === 'ScopedReview') {
                          setOpenFilter(!openFilter);
                          setActiveFilters('');
                        } else {
                          setOpenFilter(true);
                          setActiveFilters('ScopedReview');
                        }
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 28, color: '#4895ef' }} />
                      <div className='entries-frame-body-cont-lenses-head-text'>
                        Scoped Review
                      </div>
                      {openFilter && activeFilters === 'ScopedReview' ? (
                        <KeyboardArrowUpOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      ) : (
                        <KeyboardArrowDownOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      borderTop: '2px solid rgb(46, 48, 54)',
                    }}
                  />
                  <div style={{ padding: '16px' }}>
                    <ScopedReviewSummary
                      data={exportWorkflowData}
                      variant={isSectionExpanded('ScopedReview') ? 'full' : 'pill'}
                    />
                  </div>
                </div>
              )}
              {/* Where to File — PGA agency-routing summary (advisory). The first result
                  section (only Manual Review Required, when present, sits above it); self-hides
                  when pga_routing is absent (older tickets). */}
              {isExportUnified && pgaData && (
                <div
                  className='results-card-frame-comp'
                  style={{
                    width: '100%',
                    flexDirection: 'column',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    className='entries-frame-body-cont-lenses'
                    style={{ padding: '0px' }}
                  >
                    <div
                      className='entries-frame-body-cont-lenses-head'
                      onClick={() => {
                        if (activeFilters === 'WhereToFile') {
                          setOpenFilter(!openFilter);
                          setActiveFilters('');
                        } else {
                          setOpenFilter(true);
                          setActiveFilters('WhereToFile');
                        }
                      }}
                    >
                      <AccountBalanceOutlinedIcon
                        sx={{ fontSize: 28, color: '#4895EF' }}
                      />
                      <div className='entries-frame-body-cont-lenses-head-text'>
                        Where to File
                      </div>
                      {openFilter && activeFilters === 'WhereToFile' ? (
                        <KeyboardArrowUpOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      ) : (
                        <KeyboardArrowDownOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      )}
                    </div>
                  </div>
                  {isSectionCollapsed('WhereToFile') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />
                      {renderPgaRows(pgaSummaryRows)}
                    </>
                  )}
                  {isSectionExpanded('WhereToFile') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />
                      {renderPgaRows(pgaExpandedRows)}
                    </>
                  )}
                </div>
              )}
              {isExportUnified && (
                <div
                  className='results-card-frame-comp'
                  style={{
                    width: '100%',
                    flexDirection: 'column',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    className='entries-frame-body-cont-lenses'
                    style={{ padding: '0px' }}
                  >
                    <div
                      className='entries-frame-body-cont-lenses-head'
                      onClick={() => {
                        if (activeFilters === 'EEI Filing') {
                          setOpenFilter(!openFilter);
                          setActiveFilters('');
                        } else {
                          setOpenFilter(true);
                          setActiveFilters('EEI Filing');
                        }
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{ fontSize: 28, color: '#4895EF' }}
                      />
                      <div
                        className={'entries-frame-body-cont-lenses-head-text'}
                      >
                        Product Profile & EEI Check
                      </div>
                      {openFilter && activeFilters === 'EEI Filing' ? (
                        <KeyboardArrowUpOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      ) : (
                        <KeyboardArrowDownOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      )}
                    </div>
                  </div>
                  {renderAssessmentRow('profile', 'EEI Filing')}
                  {isSectionCollapsed('EEI Filing') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      ></div>
                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        {renderEeiRow(
                          'Filing Determination',
                          eeiData?.filing_determination,
                        )}
                        {renderEeiRow('Regulatory Citation', eeiData?.citation)}
                      </div>
                    </>
                  )}
                  {isSectionExpanded('EEI Filing') && (
                    <>
                      <div
                        className='results-frame-col'
                        style={{ border: '0px' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            width: '100%',
                            borderTop: '2px solid rgb(46, 48, 54)',
                          }}
                        />
                        {Object.keys(profileData).length > 0 ? (
                          <>
                            <div
                              className='results-card-frame'
                              style={{ paddingTop: '12px', paddingBottom: '0px' }}
                            >
                              {renderEeiRow(
                                'Product Description',
                                profileData?.product_description,
                              )}
                              {renderEeiRow(
                                'Export Type',
                                profileData?.export_type_display,
                              )}
                            </div>
                            <div
                              className='results-card-frame'
                              style={{ paddingTop: '12px', paddingBottom: '0px' }}
                            >
                              {renderEeiRow(
                                'Tangibility',
                                profileData?.tangible_display,
                              )}
                              {renderEeiRow(
                                'Destination Country',
                                profileData?.destination_country,
                              )}
                            </div>
                            <div
                              className='results-card-frame'
                              style={{ paddingTop: '12px', paddingBottom: '0px' }}
                            >
                              {renderEeiRow(
                                'Schedule B / HTS Code',
                                profileData?.normalized_code ||
                                  profileData?.provided_code,
                              )}
                              {renderEeiRow(
                                'Specially Designed',
                                profileData?.specially_designed_answer,
                              )}
                            </div>
                          </>
                        ) : null}
                        <div
                          className='results-card-frame'
                          style={{ paddingTop: '12px', paddingBottom: '0px' }}
                        >
                          {renderEeiRow(
                            'Filing Determination',
                            eeiData?.filing_determination,
                          )}
                          {renderEeiRow(
                            'Determination Status',
                            eeiData?.determination_status,
                          )}
                        </div>
                        <div
                          className='results-card-frame'
                          style={{ paddingTop: '12px', paddingBottom: '0px' }}
                        >
                          {renderEeiRow('Basis', eeiData?.basis)}
                          {renderEeiRow(
                            'Regulatory Citation',
                            eeiData?.citation,
                          )}
                        </div>
                        <div
                          className='results-card-frame'
                          style={{ paddingTop: '12px', paddingBottom: '0px' }}
                        >
                          {renderEeiRow(
                            'Transaction Type',
                            eeiData?.transaction_type,
                          )}
                          {renderEeiRow(
                            'Destination Category',
                            eeiData?.destination_class_display,
                          )}
                        </div>
                        <div
                          className='results-card-frame'
                          style={{ paddingTop: '12px', paddingBottom: '0px' }}
                        >
                          {renderEeiRow(
                            'Value Exemption',
                            eeiData?.value_exemption,
                            showEeiValueExemptionNote(eeiData) ? EEI_VALUE_EXEMPTION_NOTE : null,
                          )}
                          {renderEeiRow(
                            'Does a license requirement force filing?',
                            eeiData?.license_override,
                          )}
                        </div>
                        <div
                          className='results-card-frame'
                          style={{ paddingTop: '12px', paddingBottom: '0px' }}
                        >
                          {renderEeiRow(
                            'AES Exemption Legend',
                            eeiData?.aes_exemption_legend,
                          )}
                          {renderEeiRow('Next Step', eeiData?.next_step)}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div
                className='results-card-frame-comp'
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  marginBottom: '24px',
                }}
              >
                <div
                  className='entries-frame-body-cont-lenses'
                  style={{ padding: '0px' }}
                >
                  <div
                    className='entries-frame-body-cont-lenses-head'
                    onClick={() => {
                      if (activeFilters === 'ControlB') {
                        setOpenFilter(!openFilter);
                        setActiveFilters('');
                      } else {
                        setOpenFilter(true);
                        setActiveFilters('ControlB');
                      }
                    }}
                  >
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 28, color: '#4895EF' }}
                    />
                    <div className={'entries-frame-body-cont-lenses-head-text'}>
                      Schedule B / HTS Classification
                    </div>
                    {openFilter && activeFilters === 'ControlB' ? (
                      <KeyboardArrowUpOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    ) : (
                      <KeyboardArrowDownOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    )}
                  </div>
                </div>
                {renderAssessmentRow('schedule_b', 'ControlB')}
                {scheduleBNotApplicable && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify))',
                          }}
                        >
                          {'Status'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {scheduleBNaReason}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {!scheduleBNotApplicable && isSectionCollapsed('ControlB') && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify))',
                          }}
                        >
                          {'Primary Candidate - Product:'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                display: 'block',
                                width: '449px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {cur_case.data?.workflowData[0]?.schedule_b
                                ?.product_name || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify))',
                          }}
                        >
                          {'Primary Candidate - Schedule B Code'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {
                                cur_case.data?.workflowData[0]?.schedule_b
                                  ?.schedule_b_code
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {!scheduleBNotApplicable && isSectionExpanded('ControlB') && (
                  <>
                    <div
                      className='results-frame-col'
                      ref={componentRef}
                      style={{ border: '0px' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Primary Candidate - Product:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]?.schedule_b
                                  ?.product_name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {scheduleBCodeRows(cur_case.data?.workflowData[0]?.schedule_b)
                              .exportCode
                              ? 'Export code'
                              : 'Primary Candidate - Schedule B Code'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {scheduleBCodeRows(
                                  cur_case.data?.workflowData[0]?.schedule_b,
                                ).exportCode ||
                                  cur_case.data?.workflowData[0]?.schedule_b
                                    ?.schedule_b_code ||
                                  'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Slice B: how the user's own code was checked — same 2-column pattern
                          (renderEeiRow + itarSpacerCol); hidden when classified from the
                          description (both fields absent). */}
                      {(scheduleBCodeRows(cur_case.data?.workflowData[0]?.schedule_b)
                        .aesCheck ||
                        scheduleBCodeRows(cur_case.data?.workflowData[0]?.schedule_b)
                          .codeSource) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {scheduleBCodeRows(cur_case.data?.workflowData[0]?.schedule_b)
                            .aesCheck
                            ? renderEeiRow(
                                'AES Export Check:',
                                scheduleBCodeRows(
                                  cur_case.data?.workflowData[0]?.schedule_b,
                                ).aesCheck,
                              )
                            : itarSpacerCol}
                          {scheduleBCodeRows(cur_case.data?.workflowData[0]?.schedule_b)
                            .codeSource
                            ? renderEeiRow(
                                'Where the Code Came From:',
                                scheduleBCodeRows(
                                  cur_case.data?.workflowData[0]?.schedule_b,
                                ).codeSource,
                              )
                            : itarSpacerCol}
                        </div>
                      )}

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Primary Candidate - Section'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]?.schedule_b
                                  ?.section || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Primary Candidate - Chapter'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]?.schedule_b
                                  ?.chapter || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Selection Rationale | Closest Alternative Considered — rendered with
                          the SAME 2-column pattern as the ITAR rows (renderEeiRow + itarSpacerCol
                          in a results-card-frame with alignItems:'normal'). The two columns
                          constrain each cell to ~50%, so the long text wraps instead of
                          overflowing (a single full-width cell did not wrap). */}
                      {(cur_case.data?.workflowData[0]?.schedule_b
                        ?.selection_rationale ||
                        cur_case.data?.workflowData[0]?.schedule_b
                          ?.closest_alternative_hs_code ||
                        cur_case.data?.workflowData[0]?.schedule_b
                          ?.closest_alternative_reason) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {cur_case.data?.workflowData[0]?.schedule_b
                            ?.selection_rationale
                            ? renderEeiRow(
                                'Selection Rationale:',
                                cur_case.data?.workflowData[0]?.schedule_b
                                  ?.selection_rationale,
                              )
                            : itarSpacerCol}
                          {cur_case.data?.workflowData[0]?.schedule_b
                            ?.closest_alternative_hs_code ||
                          cur_case.data?.workflowData[0]?.schedule_b
                            ?.closest_alternative_reason
                            ? renderEeiRow(
                                'Closest Alternative Considered:',
                                [
                                  cur_case.data?.workflowData[0]?.schedule_b
                                    ?.closest_alternative_hs_code,
                                  cur_case.data?.workflowData[0]?.schedule_b
                                    ?.closest_alternative_reason,
                                ]
                                  .filter(Boolean)
                                  .join(' — '),
                              )
                            : itarSpacerCol}
                        </div>
                      )}

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Needs Clarification:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {`${
                                  cur_case.data?.workflowData[0]?.schedule_b
                                    ?.needs_clarification || 'N/A'
                                }`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div
                className='results-card-frame-comp'
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  marginBottom: '24px',
                }}
              >
                <div
                  className='entries-frame-body-cont-lenses'
                  style={{ padding: '0px' }}
                >
                  <div
                    className='entries-frame-body-cont-lenses-head'
                    onClick={() => {
                      if (activeFilters === 'Control') {
                        setOpenFilter(!openFilter);
                        setActiveFilters('');
                      } else {
                        setOpenFilter(true);
                        setActiveFilters('Control');
                      }
                    }}
                  >
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 28, color: '#4895EF' }}
                    />
                    <div className={'entries-frame-body-cont-lenses-head-text'}>
                      Country and Control List Check
                    </div>
                    {openFilter && activeFilters === 'Control' ? (
                      <KeyboardArrowUpOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    ) : (
                      <KeyboardArrowDownOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    )}
                  </div>
                </div>
                {renderAssessmentRow('country', 'Control')}
                {isSectionCollapsed('Control') && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify))',
                          }}
                        >
                          {'Schedule B Code: '}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                display: 'block',
                                width: '449px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {cur_case.data?.commodity?.eccn}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify))',
                          }}
                        >
                          {'Destination Country'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {cur_case.data?.workflowData[0]?.country_screening
                                ?.normalized_country_name || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {isSectionExpanded('Control') && (
                  <>
                    <div
                      className='results-frame-col'
                      ref={componentRef}
                      style={{ border: '0px' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Input Destination Country:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]
                                  ?.country_screening
                                  ?.normalized_country_name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Normalized Country'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]
                                  ?.country_screening
                                  ?.normalized_country_name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'ISO2:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]
                                  ?.country_screening?.country_iso2 || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Is this Country Embargoed'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {`${formatYesNo(
                                  cur_case.data?.workflowData[0]
                                    ?.country_screening?.embargoed_countries,
                                )}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify)ext-classify)ext-classify))',
                            }}
                          >
                            {'Is this Country OFAC-Sanctioned:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {`${formatYesNo(
                                  cur_case.data?.workflowData[0]
                                    ?.country_screening
                                    ?.sanctions_programs_and_country_info,
                                )}`}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify)ext-classify))',
                            }}
                          >
                            {
                              'Is this a BIS Russia / Belarus Transshipment Point'
                            }
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {formatYesNo(
                                  cur_case.data?.workflowData[0]
                                    ?.country_screening
                                    ?.transhipment_points_russia_belarus,
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Is this a BIS China Transshipment Point'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {formatYesNo(
                                  cur_case.data?.workflowData[0]
                                    ?.country_screening
                                    ?.transhipment_points_china,
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Embargo Nature'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  width: '449px',
                                  maxWidth: '100%',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {exportWorkflowData?.country_screening
                                  ?.embargo_nature || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Embargo Detailed Summary'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {exportWorkflowData?.country_screening
                                  ?.embargo_detailed_summary || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* {Boolean(
                      cur_case.data?.eccnResults.find(
                        (item: { section: string }) =>
                          item.section === 'license_requirement_note',
                      ),
                    ) && (
                      <div
                        className='classify-res-comp-body'
                        style={{ background: 'transparent' }}
                      >
                        <ECCNWorkflowForm
                          userHasSearch={true}
                          hasLicense={Boolean(
                            cur_case.data?.eccnResults.find(
                              (item: { section: string }) =>
                                item.section === 'license_requirement_note',
                            ),
                          )}
                          licenseMsg={
                            cur_case.data?.eccnResults.find(
                              (item: { section: string }) =>
                                item.section === 'license_requirement_note',
                            ) || []
                          }
                        />
                      </div>
                    )} */}
                      {/* <div style={{ paddingTop: '10px' }}>
                      <StreamRDisplay
                        message={[ ...cur_case.data?.eccnResults ].sort((a, b) => {
                          const aIndex = preferredOrder.indexOf(a.result?.heading);
                          const bIndex = preferredOrder.indexOf(b.result?.heading);

                          // If both are in preferredOrder, compare their indexes
                          if (aIndex !== -1 && bIndex !== -1) {
                            return aIndex - bIndex;
                          }

                          // If only one is in preferredOrder, that one comes first
                          if (aIndex !== -1) {
                            return -1;
                          }
                          if (bIndex !== -1) {
                            return 1;
                          }

                          // Otherwise, keep their original order
                          return 0;
                        })}
                        ticket={false}
                      />
                    </div> */}
                    </div>
                  </>
                )}
                {/* Control-list part of the Country and Control List Check (the pre-merge
                    "Screening Results" / Sensitive Tech section). Appendix X niche-PGA
                    requirements render INSIDE EARScreeningResults. */}
                {isExportUnified && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <EARScreeningResults
                        workflowData={exportWorkflowData}
                        primary={exportScreeningPrimary}
                        country={exportScreeningCountry}
                        isSelected={false}
                        isExpands={isSectionExpanded('Control')}
                        handleRowClick={() => undefined}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Jurisdiction Routing — the v2 PACKAGE-level disposition (stopped / continued /
                  unresolved) + reasons + manual-review caution. Collapsed shows the pill only;
                  expanded shows the full badge. Sits directly above Nuclear (NRC + DOE), where the
                  jurisdiction checks begin — same order as the live Summary Results card.
                  Self-hides when absent (flag OFF / older ticket). */}
              {isExportUnified && jurisdictionData && (
                <div
                  className='results-card-frame-comp'
                  style={{
                    width: '100%',
                    flexDirection: 'column',
                    marginBottom: '24px',
                  }}
                >
                  <div
                    className='entries-frame-body-cont-lenses'
                    style={{ padding: '0px' }}
                  >
                    <div
                      className='entries-frame-body-cont-lenses-head'
                      onClick={() => {
                        if (activeFilters === 'JurisdictionRouting') {
                          setOpenFilter(!openFilter);
                          setActiveFilters('');
                        } else {
                          setOpenFilter(true);
                          setActiveFilters('JurisdictionRouting');
                        }
                      }}
                    >
                      <PolicyOutlinedIcon
                        sx={{ fontSize: 28, color: '#4895EF' }}
                      />
                      <div className='entries-frame-body-cont-lenses-head-text'>
                        Jurisdiction Routing
                      </div>
                      {openFilter && activeFilters === 'JurisdictionRouting' ? (
                        <KeyboardArrowUpOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      ) : (
                        <KeyboardArrowDownOutlinedIcon
                          sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                        />
                      )}
                    </div>
                  </div>
                  {isSectionCollapsed('JurisdictionRouting') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />
                      <div style={{ padding: '16px' }}>
                        <JurisdictionDispositionBadge
                          routing={jurisdictionData}
                          variant='pill'
                        />
                      </div>
                    </>
                  )}
                  {isSectionExpanded('JurisdictionRouting') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />
                      <div style={{ padding: '16px' }}>
                        <JurisdictionDispositionBadge routing={jurisdictionData} />
                      </div>
                    </>
                  )}
                </div>
              )}

              <div
                className='results-card-frame-comp'
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  marginBottom: '24px',
                }}
              >
                <div
                  className='entries-frame-body-cont-lenses'
                  style={{ padding: '0px' }}
                >
                  <div
                    className='entries-frame-body-cont-lenses-head'
                    onClick={() => {
                      if (activeFilters === 'ControlNRC') {
                        setOpenFilter(!openFilter);
                        setActiveFilters('');
                      } else {
                        setOpenFilter(true);
                        setActiveFilters('ControlNRC');
                      }
                    }}
                  >
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 28, color: '#4895EF' }}
                    />
                    <div className={'entries-frame-body-cont-lenses-head-text'}>
                      Nuclear (NRC + DOE)
                    </div>
                    {openFilter && activeFilters === 'ControlNRC' ? (
                      <KeyboardArrowUpOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    ) : (
                      <KeyboardArrowDownOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    )}
                  </div>
                </div>
                {renderNotRequestedRow('nrc')}
                {isSectionCollapsed('ControlNRC') && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify))',
                          }}
                        >
                          {'Control Result:'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                display: 'block',
                                width: '449px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {nuclearControlResult()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {isSectionExpanded('ControlNRC') && (
                  <>
                    <div
                      className='results-frame-col'
                      ref={componentRef}
                      style={{ border: '0px' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />

                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        {renderEeiRow('Control Result:', nuclearControlResult())}
                        {renderEeiRow('Reason:', nrcData?.reason)}
                      </div>

                      {/* v2 conditional-routing — nuclear jurisdiction coverage (spans
                          NRC + DOE). Own 2-col row + spacer. Self-hides when absent. */}
                      {nrcCoverageStatus && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {renderCoverageRow(
                            'Nuclear jurisdiction coverage:',
                            nrcCoverageStatus,
                            'nuclear',
                          )}
                          {itarSpacerCol}
                        </div>
                      )}

                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        {nrcData?.confidence
                          ? renderEeiRow('Confidence:', capFirst(nrcData?.confidence))
                          : itarSpacerCol}
                        {renderEeiRow('Notes:', nrcData?.notes)}
                      </div>

                      {(nrcClosestAlt || nrcAssumptions.length > 0) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {nrcClosestAlt
                            ? renderEeiRow('Closest Alternative Considered:', nrcClosestAlt)
                            : itarSpacerCol}
                          {nrcAssumptions.length > 0
                            ? renderItarListCol('Assumptions:', nrcAssumptions)
                            : itarSpacerCol}
                        </div>
                      )}
                      {/* Missing information — its OWN field (parity with ITAR / CCL). Self-hides. */}
                      {nrcMissingInfo.length > 0 && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {renderItarListCol('Missing information:', nrcMissingInfo)}
                          {itarSpacerCol}
                        </div>
                      )}
                      {cur_case.data?.workflowData[0]?.nrc?.doe_controlled && (
                        <>
                          <div
                            className='results-card-frame'
                            style={{
                              paddingTop: '12px',
                              paddingBottom: '0px',
                              alignItems: 'normal',
                            }}
                          >
                            {renderEeiRow('DOE Part 810 Result:', nrcData?.doe_controlled)}
                            {renderEeiRow('DOE Reason:', nrcData?.doe_reason)}
                          </div>

                          {(nrcData?.doe_confidence || nrcDoeClosestAlt) && (
                            <div
                              className='results-card-frame'
                              style={{
                                paddingTop: '12px',
                                paddingBottom: '0px',
                                alignItems: 'normal',
                              }}
                            >
                              {nrcData?.doe_confidence
                                ? renderEeiRow('DOE Confidence:', capFirst(nrcData?.doe_confidence))
                                : itarSpacerCol}
                              {nrcDoeClosestAlt
                                ? renderEeiRow('DOE Closest Alternative Considered:', nrcDoeClosestAlt)
                                : itarSpacerCol}
                            </div>
                          )}

                          {nrcDoeMissingInfo.length > 0 && (
                            <div
                              className='results-card-frame'
                              style={{
                                paddingTop: '12px',
                                paddingBottom: '0px',
                                alignItems: 'normal',
                              }}
                            >
                              {renderItarListCol(
                                'DOE Missing information:',
                                nrcDoeMissingInfo,
                              )}
                              {itarSpacerCol}
                            </div>
                          )}
                          {nrcDoeAssumptions.length > 0 && (
                            <div
                              className='results-card-frame'
                              style={{
                                paddingTop: '12px',
                                paddingBottom: '0px',
                                alignItems: 'normal',
                              }}
                            >
                              {renderItarListCol('DOE Assumptions:', nrcDoeAssumptions)}
                              {itarSpacerCol}
                            </div>
                          )}
                          {cur_case.data?.workflowData[0]?.nrc?.doe_notes && (
                            <div
                              className='results-card-frame'
                              style={{
                                paddingTop: '12px',
                                paddingBottom: '0px',
                                alignItems: 'normal',
                              }}
                            >
                              {renderEeiRow('DOE Notes:', nrcData?.doe_notes)}
                              <div className='tariff-inputs' />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div
                className='results-card-frame-comp'
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  marginBottom: '24px',
                }}
              >
                <div
                  className='entries-frame-body-cont-lenses'
                  style={{ padding: '0px' }}
                >
                  <div
                    className='entries-frame-body-cont-lenses-head'
                    onClick={() => {
                      if (activeFilters === 'ControlITAR') {
                        setOpenFilter(!openFilter);
                        setActiveFilters('');
                      } else {
                        setOpenFilter(true);
                        setActiveFilters('ControlITAR');
                      }
                    }}
                  >
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 28, color: '#4895EF' }}
                    />
                    <div className={'entries-frame-body-cont-lenses-head-text'}>
                      ITAR Check
                    </div>
                    {openFilter && activeFilters === 'ControlITAR' ? (
                      <KeyboardArrowUpOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    ) : (
                      <KeyboardArrowDownOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    )}
                  </div>
                </div>
                {renderNotRequestedRow('itar')}
                {isSectionCollapsed('ControlITAR') && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify))',
                          }}
                        >
                          {'Control Result:'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                display: 'block',
                                width: '449px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {cur_case.data?.workflowData[0]?.itar
                                ?.controlled || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {isSectionExpanded('ControlITAR') && (
                  <>
                    <div
                      className='results-frame-col'
                      ref={componentRef}
                      style={{ border: '0px' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />

                      {/* Control Result | USML category (+ paragraph, SME/MT) */}
                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        {renderEeiRow('Control Result:', itarData?.controlled)}
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'USML category:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {itarData?.usml_category || 'N/A'}
                              </div>
                              {itarData?.usml_paragraph ? (
                                <div
                                  style={{
                                    marginTop: '4px',
                                    color: 'var(--text-primary-color)',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                  }}
                                >
                                  {`Paragraph ${itarData.usml_paragraph}`}
                                  {itarData?.sme ? (
                                    <span
                                      style={itarBadge(
                                        'var(--low-color, #dc2626)',
                                      )}
                                    >
                                      SME
                                    </span>
                                  ) : null}
                                  {itarData?.mt_controlled ? (
                                    <span
                                      style={itarBadge(
                                        'var(--mid-color, #f59e0b)',
                                      )}
                                    >
                                      MT
                                    </span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* v2 conditional-routing — ITAR jurisdiction coverage. Own 2-col
                          row + spacer. Self-hides when absent. */}
                      {itarCoverageStatus && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {renderCoverageRow(
                            'ITAR jurisdiction coverage:',
                            itarCoverageStatus,
                            'itar',
                          )}
                          {itarSpacerCol}
                        </div>
                      )}

                      {/* Reason | Confidence */}
                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        {renderEeiRow('Reason:', itarData?.reason)}
                        {renderEeiRow('Confidence:', itarData?.confidence)}
                      </div>

                      {itarClosestAlt && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {renderEeiRow('Closest Alternative Considered:', itarClosestAlt)}
                          {itarSpacerCol}
                        </div>
                      )}

                      {/* Destination policy | License note */}
                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        {renderEeiRow(
                          'Destination policy:',
                          itarData?.country_policy_note,
                        )}
                        {renderEeiRow('License note:', itarData?.license_note)}
                      </div>

                      {/* Exemption outlook (AUKUS §126.7 / treaty) | FMS channel (§126.6) */}
                      {(itarData?.exemption_note ||
                        itarData?.fms_channel === true) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {itarData?.exemption_note
                            ? renderEeiRow(
                                'Exemption outlook:',
                                itarData.exemption_note,
                              )
                            : itarSpacerCol}
                          {itarData?.fms_channel === true
                            ? renderEeiRow(
                                'FMS channel (§126.6):',
                                'Foreign Military Sales — follows the DoD/DSCA Letter of Offer and Acceptance rather than a standard DSP license.',
                              )
                            : itarSpacerCol}
                        </div>
                      )}

                      {/* Matched USML text | How this was assessed */}
                      {(itarMatchedSnippets.length > 0 ||
                        itarReviewTrace.length > 0) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {itarMatchedSnippets.length > 0
                            ? renderItarListCol(
                                'Matched USML text:',
                                itarMatchedSnippets,
                              )
                            : itarSpacerCol}
                          {itarReviewTrace.length > 0
                            ? renderItarListCol(
                                'How this was assessed:',
                                itarReviewTrace,
                              )
                            : itarSpacerCol}
                        </div>
                      )}

                      {/* Missing information | Assumptions */}
                      {(itarMissingInfo.length > 0 ||
                        itarAssumptions.length > 0) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {itarMissingInfo.length > 0
                            ? renderItarListCol(
                                'Missing information:',
                                itarMissingInfo,
                              )
                            : itarSpacerCol}
                          {itarAssumptions.length > 0
                            ? renderItarListCol('Assumptions:', itarAssumptions)
                            : itarSpacerCol}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div
                className='results-card-frame-comp'
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  marginBottom: '24px',
                }}
              >
                <div
                  className='entries-frame-body-cont-lenses'
                  style={{ padding: '0px' }}
                >
                  <div
                    className='entries-frame-body-cont-lenses-head'
                    onClick={() => {
                      if (activeFilters === 'ControlEAR') {
                        setOpenFilter(!openFilter);
                        setActiveFilters('');
                      } else {
                        setOpenFilter(true);
                        setActiveFilters('ControlEAR');
                      }
                    }}
                  >
                    <LocalOfferOutlinedIcon
                      sx={{ fontSize: 28, color: '#4895EF' }}
                    />
                    <div className={'entries-frame-body-cont-lenses-head-text'}>
                      EAR / CCL Check
                    </div>
                    {openFilter && activeFilters === 'ControlEAR' ? (
                      <KeyboardArrowUpOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    ) : (
                      <KeyboardArrowDownOutlinedIcon
                        sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                      />
                    )}
                  </div>
                </div>
                {renderAssessmentRow('ccl', 'ControlEAR')}
                {isSectionCollapsed('ControlEAR') && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        borderTop: '2px solid rgb(46, 48, 54)',
                      }}
                    ></div>
                    <div
                      className='results-card-frame'
                      style={{ paddingTop: '12px', paddingBottom: '0px' }}
                    >
                      <div className='tariff-inputs'>
                        <div
                          className='classify-header-ticket'
                          style={{
                            color:
                              'var(--Text-Secondary, var(--text-classify))',
                          }}
                        >
                          {'Classification Result:'}
                        </div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div
                              className='classify-cont-sec-div'
                              style={{
                                display: 'block',
                                width: '449px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                                color: 'var(--text-primary-color)',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              {cur_case.data?.workflowData[0]?.ear_ccl
                                ?.classification_result || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {isSectionExpanded('ControlEAR') && (
                  <>
                    <div
                      className='results-frame-col'
                      ref={componentRef}
                      style={{ border: '0px' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      />

                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Classification Result:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]?.ear_ccl
                                  ?.classification_result || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Reason:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]?.ear_ccl
                                  ?.reasoning || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {(earCclConfidence || earCclCandidates) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {earCclConfidence
                            ? renderEeiRow('Confidence:', earCclConfidence)
                            : itarSpacerCol}
                          {earCclCandidates
                            ? renderEeiRow('Candidate ECCNs:', earCclCandidates)
                            : itarSpacerCol}
                        </div>
                      )}

                      {(earCclClosestAlt || earCclAssumptions.length > 0) && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {earCclClosestAlt
                            ? renderEeiRow('Closest Alternative Considered:', earCclClosestAlt)
                            : itarSpacerCol}
                          {earCclAssumptions.length > 0
                            ? renderItarListCol('Assumptions:', earCclAssumptions)
                            : itarSpacerCol}
                        </div>
                      )}

                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'License requirement:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.workflowData[0]?.ear_ccl
                                  ?.license_requirement || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Scope and order of review:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {scopeAndOrderText || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'9x515 / 600-series review:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {review9x515600Text || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Specially designed review:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {speciallyDesignedText || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className='results-card-frame'
                        style={{
                          paddingTop: '12px',
                          paddingBottom: '0px',
                          alignItems: 'normal',
                        }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Recommended ECCN Review:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  maxWidth: '100%',
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {candidateEccnText || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'License review:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {licenseReviewText || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Missing information — its OWN aligned row (parity with ITAR / NRC),
                          not a third cell in the Recommended-ECCN / License-review row (which
                          broke the 2-column grid). Self-hides when empty; spacer keeps 50/50. */}
                      {cclMissingInfoText && (
                        <div
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          <div className='tariff-inputs'>
                            <div
                              className='classify-header-ticket'
                              style={{
                                color:
                                  'var(--Text-Secondary, var(--text-classify))',
                              }}
                            >
                              {'Missing information:'}
                            </div>
                            <div
                              className='classify-cont'
                              style={{ padding: '0px' }}
                            >
                              <div className='classify-cont-sec'>
                                <div
                                  className='classify-cont-sec-div'
                                  style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    maxWidth: '100%',
                                    textOverflow: 'ellipsis',
                                    color: 'var(--text-primary-color)',
                                    fontWeight: '600',
                                    fontSize: '16px',
                                  }}
                                >
                                  {cclMissingInfoText}
                                </div>
                              </div>
                            </div>
                          </div>
                          {itarSpacerCol}
                        </div>
                      )}

                      {/* Lower CCL detail sections — End-use / International-Multilateral / BIS
                          guidance — laid out TWO-PER-ROW (results-card-frame + two tariff-inputs)
                          so they align with the paired sections above (Recommended ECCN + License
                          review) instead of stacking full-width. cclLowerCells / cclLowerRows are
                          built in the component body; each optional cell self-hides and a lone
                          trailing cell keeps the left column with a spacer. */}
                      {cclLowerRows.map((row) => (
                        <div
                          key={row.map((c) => c.label).join('|')}
                          className='results-card-frame'
                          style={{
                            paddingTop: '12px',
                            paddingBottom: '0px',
                            alignItems: 'normal',
                          }}
                        >
                          {row.map((cell) => (
                            <div className='tariff-inputs' key={cell.label}>
                              <div
                                className='classify-header-ticket'
                                style={{
                                  color:
                                    'var(--Text-Secondary, var(--text-classify))',
                                }}
                              >
                                {cell.label}
                              </div>
                              <div
                                className='classify-cont'
                                style={{ padding: '0px' }}
                              >
                                <div className='classify-cont-sec'>
                                  <div
                                    className='classify-cont-sec-div'
                                    style={cell.style}
                                  >
                                    {cell.value}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          {row.length === 1 && (
                            <div className='tariff-inputs' aria-hidden='true' />
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Tariffs UI */}
          {['Import Unified'].includes(cur_case.data.docType) && (
            <div
              className='results-card-frame-comp'
              style={{
                width: '100%',
                flexDirection: 'column',
                marginBottom: '24px',
              }}
            >
              <div
                className='entries-frame-body-cont-lenses'
                style={{ padding: '0px' }}
              >
                <div
                  className='entries-frame-body-cont-lenses-head'
                  onClick={() => {
                    if (activeFilters === 'Tariffs') {
                      setOpenFilter(!openFilter);
                      setActiveFilters('');
                    } else {
                      setOpenFilter(true);
                      setActiveFilters('Tariffs');
                    }
                  }}
                >
                  <AttachMoneyOutlinedIcon
                    sx={{ fontSize: 28, color: '#4895EF' }}
                  />
                  <div className={'entries-frame-body-cont-lenses-head-text'}>
                    Tariffs
                  </div>
                  {openFilter && activeFilters === 'Tariffs' ? (
                    <KeyboardArrowUpOutlinedIcon
                      sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                    />
                  ) : (
                    <KeyboardArrowDownOutlinedIcon
                      sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                    />
                  )}
                </div>
              </div>
              {isSectionCollapsed('Tariffs') && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      borderTop: '2px solid rgb(46, 48, 54)',
                    }}
                  ></div>
                  <div
                    className='results-card-frame'
                    style={{
                      paddingTop: '12px',
                      paddingBottom: '0px',
                      display: isPdfExpandingUnified ? 'block' : undefined,
                      width: '100%',
                    }}
                  >
                    <div className='tariff-inputs'>
                      <div
                        className='classify-header-ticket'
                        style={{
                          color: 'var(--Text-Secondary, var(--text-classify))',
                        }}
                      >
                        {'Base Duty Cost:'}
                      </div>
                      <div className='classify-cont' style={{ padding: '0px' }}>
                        <div className='classify-cont-sec'>
                          <div
                            className='classify-cont-sec-div'
                            style={{
                              color: 'var(--text-primary-color)',
                              fontWeight: '600',
                              fontSize: '16px',
                            }}
                          >
                            ${totalBaseCost || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tariff-inputs'>
                      <div
                        className='classify-header-ticket'
                        style={{
                          color: 'var(--Text-Secondary, var(--text-classify))',
                        }}
                      >
                        {'Additional Duty Rate:'}
                      </div>
                      <div className='classify-cont' style={{ padding: '0px' }}>
                        <div className='classify-cont-sec'>
                          <div
                            className='classify-cont-sec-div'
                            style={{
                              color: 'var(--text-primary-color)',
                              fontWeight: '600',
                              fontSize: '16px',
                            }}
                          >
                            {totalAddCost || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='tariff-inputs'>
                      <div
                        className='classify-header-ticket'
                        style={{
                          color: 'var(--Text-Secondary, var(--text-classify))',
                        }}
                      >
                        {'Total Cost:'}
                      </div>
                      <div className='classify-cont' style={{ padding: '0px' }}>
                        <div className='classify-cont-sec'>
                          <div
                            className='classify-cont-sec-div'
                            style={{
                              color: 'var(--text-primary-color)',
                              fontWeight: '600',
                              fontSize: '16px',
                            }}
                          >
                            ${totalCost || '0'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {isSectionExpanded('Tariffs') && (
                <>
                  <div
                    className='results-card-frame'
                    style={{
                      paddingTop: '12px',
                      paddingBottom: '0px',
                      display: isPdfExpandingUnified ? 'block' : undefined,
                      width: '100%',
                    }}
                  >
                    {isPdfExpandingUnified ? (
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          gap: '0px',
                        }}
                      >
                        <div style={{ width: '100%', display: 'block' }}>
                          {renderPdfTariffInputs()}
                        </div>
                        {renderPdfTariffInsights()}
                        <div
                          style={{
                            width: '100%',
                            display: 'block',
                            borderTop: '2px solid var(--bg-secondary)',
                            paddingTop: '24px',
                          }}
                        >
                          <TarrifResults
                            tariffResults={cur_case.data.tariffResults}
                            productValue={
                              Number(
                                cur_case?.data?.tariffResults?.inputs
                                  ?.productValue,
                              ) || 0
                            }
                            explanationText=''
                            aiLoading={false}
                            adcvdResults={
                              cur_case?.data?.tariffResults?.adcvdResults
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <TariffPage
                        forceStatic={true}
                        savedTariffResult={cur_case.data.tariffResults}
                        tariffJurisdiction={cur_case.data.tariffJurisdiction}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {!isExportUnified && (
            <div
              className='results-card-frame-comp'
              style={{
                width: '100%',
                flexDirection: 'column',
                marginBottom: '24px',
              }}
            >
              <div
                className='entries-frame-body-cont-lenses'
                style={{ padding: '0px' }}
              >
                <div
                  className='entries-frame-body-cont-lenses-head'
                  onClick={() => {
                    if (activeFilters === 'Screening Results') {
                      setOpenFilter(!openFilter);
                      setActiveFilters('');
                    } else {
                      setOpenFilter(true);
                      setActiveFilters('Screening Results');
                    }
                  }}
                >
                  <GppMaybeOutlinedIcon
                    sx={{ fontSize: 28, color: '#4895EF' }}
                  />
                  <div className={'entries-frame-body-cont-lenses-head-text'}>
                    Screening Results
                  </div>
                  {openFilter && activeFilters === 'Screening Results' ? (
                    <KeyboardArrowUpOutlinedIcon
                      sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                    />
                  ) : (
                    <KeyboardArrowDownOutlinedIcon
                      sx={{ fontSize: 28, color: 'var(--text-primary)' }}
                    />
                  )}
                </div>
              </div>
              {isExportUnified ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      borderTop: '2px solid rgb(46, 48, 54)',
                    }}
                  ></div>
                  <div
                    className='results-card-frame'
                    style={{ paddingTop: '12px', paddingBottom: '0px' }}
                  >
                    <EARScreeningResults
                      workflowData={exportWorkflowData}
                      primary={exportScreeningPrimary}
                      country={exportScreeningCountry}
                      isSelected={false}
                      isExpands={isSectionExpanded('Screening Results')}
                      handleRowClick={() => undefined}
                    />
                  </div>
                </>
              ) : (
                <>
                  {isSectionCollapsed('Screening Results') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          width: '100%',
                          borderTop: '2px solid rgb(46, 48, 54)',
                        }}
                      ></div>
                      <div
                        className='results-card-frame'
                        style={{ paddingTop: '12px', paddingBottom: '0px' }}
                      >
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Risk Level:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.aiSums[0]?.risk_level || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Regulatory Burden:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {cur_case.data?.aiSums[0]?.burden || '-'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='tariff-inputs'>
                          <div
                            className='classify-header-ticket'
                            style={{
                              color:
                                'var(--Text-Secondary, var(--text-classify))',
                            }}
                          >
                            {'Total Results:'}
                          </div>
                          <div
                            className='classify-cont'
                            style={{ padding: '0px' }}
                          >
                            <div className='classify-cont-sec'>
                              <div
                                className='classify-cont-sec-div'
                                style={{
                                  color: 'var(--text-primary-color)',
                                  fontWeight: '600',
                                  fontSize: '16px',
                                }}
                              >
                                {passedDownResults[0]?.hits || '0'} entries
                                found
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {isSectionExpanded('Screening Results') && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '30px',
                          paddingBottom: '22px',
                          width: '100%',
                        }}
                      >
                        <AIEvalProduct
                          aiSums={
                            cur_case.data?.aiSums ? cur_case.data?.aiSums : []
                          }
                          forceShowAiEvaluation={isPdfExpandingUnified}
                        />
                      </div>
                      <div className='entity-screening-results-heading'>
                        <h2>Screening Results</h2>
                        <p>
                          Product matches across selected lenses. Click any row
                          to view match details.
                        </p>
                      </div>
                      <div className='entity-screening-results-shell'>
                        <EntityScreeningResultsTable
                          isStatic
                          variant='product'
                          handleSubmit={() => undefined}
                          setPage={setProductTablePage}
                          page={productTablePage}
                          results={productScreeningResults}
                          searchTerm={cur_case.data?.ticketName || ''}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Modal open={isApprovalModalOpen} onClose={handleApprovalCloseModal}>
        <ApprovalStatus
          handleClose={handleApprovalCloseModal}
          changeStatus={changeStatus}
          curStatus={cur_case.data?.status}
        />
      </Modal>
    </>
  );
};

export default ConsolidatedResults;
